// ─── Production-grade i18n translations ──────────────────────────────
// All user-facing strings for pt-BR, en, and es.
// Every key is typed — TypeScript will catch missing translations at compile time.

export interface Translations {
  // ── Navigation ──
  home: string;
  courses: string;
  categories: string;
  forCompanies: string;
  about: string;
  login: string;
  register: string;
  logout: string;
  myCourses: string;
  myProfile: string;
  helpAndSupport: string;
  certificates: string;
  search: string;
  searchPlaceholder: string;
  faq: string;

  // ── Categories ──
  quality: string;
  environment: string;
  workSafety: string;
  infoSecurity: string;
  automotive: string;
  foodSafety: string;
  technology: string;
  businessContinuity: string;
  compliance: string;

  // ── Common ──
  viewDetails: string;
  enrollNow: string;
  startNow: string;
  learnMore: string;
  cart: string;
  emptyCart: string;
  goToCheckout: string;
  searchCourses: string;
  remove: string;
  total: string;
  backToHome: string;
  backToLogin: string;
  backToCourses: string;
  backToCourse: string;
  free: string;
  loading: string;
  saving: string;
  save: string;
  cancel: string;
  close: string;
  or: string;
  and: string;
  yes: string;
  no: string;

  // ── Category Pages ──
  categoryTitle: string;
  categoryDescription: string;
  noCourses: string;
  categoryNotFound: string;
  viewAllCategories: string;
  coursesIn: string;
  comingSoonCategory: string;
  exploreOtherCourses: string;
  viewCourses: string;

  // ── Categories Index ──
  categoriesHeroTitle: string;
  categoriesHeroSubtitle: string;
  categoriesNotFoundTitle: string;
  categoriesNotFoundSubtitle: string;
  exploreAllCourses: string;

  // ── Hero Section ──
  heroBadge: string;
  heroTitle1: string;
  heroTitle2: string;
  heroSubtitle: string;
  exploreCourses: string;
  corporateSolutions: string;
  availableCourses: string;
  coursesCompleted: string;
  trainedProfessionals: string;
  yearsExperience: string;

  // ── Featured Courses ──
  featuredCourses: string;
  featuredCoursesSubtitle: string;
  viewAllCourses: string;
  bestSeller: string;
  newCourse: string;
  hours: string;
  students: string;

  // ── Why DQS ──
  whyDQS: string;
  whyDQSSubtitle: string;
  globalExpertise: string;
  globalExpertiseDesc: string;
  specializedInstructors: string;
  specializedInstructorsDesc: string;
  recognizedCertificates: string;
  recognizedCertificatesDesc: string;
  practicalMethodology: string;
  practicalMethodologyDesc: string;
  continuousSupport: string;
  continuousSupportDesc: string;
  professionalCommunity: string;
  professionalCommunityDesc: string;
  alignedWithStandards: string;
  certified: string;

  // ── Categories Section ──
  exploreByCategory: string;
  exploreByCategorySubtitle: string;
  coursesCount: string;
  categorySectionQualityDesc: string;
  categorySectionEnvironmentDesc: string;
  categorySectionSafetyDesc: string;
  categorySectionInfoSecDesc: string;
  categorySectionAutomotiveDesc: string;
  categorySectionFoodDesc: string;

  // ── Corporate Section ──
  corporateSolutionsBadge: string;
  corporateTitle: string;
  corporateSubtitle: string;
  teamManagement: string;
  teamManagementDesc: string;
  detailedReports: string;
  detailedReportsDesc: string;
  corporateCertificates: string;
  corporateCertificatesDesc: string;
  learnSolutions: string;
  talkToConsultant: string;
  companiesServed: string;
  employeesTrained: string;
  satisfaction: string;
  platformAccess: string;
  exclusivePortal: string;
  realTimeReports: string;

  // ── CTA Section ──
  startYourJourney: string;
  readyToDevelop: string;
  ctaSubtitle: string;
  createFreeAccount: string;
  freeSignup: string;

  // ── Footer ──
  footerTagline: string;
  footerDescription: string;
  coursesTitle: string;
  categoriesTitle: string;
  institutionalTitle: string;
  legalTitle: string;
  aboutDQS: string;
  contact: string;
  termsOfUse: string;
  privacyPolicy: string;
  cookiePolicy: string;
  allRightsReserved: string;
  companyGroup: string;

  // ── Auth — Login ──
  welcomeBack: string;
  loginSubtitle: string;
  email: string;
  password: string;
  forgotPassword: string;
  noAccount: string;
  createAccount: string;
  loginTab: string;
  createAccountTab: string;
  loggingIn: string;
  loginButton: string;
  orContinueWith: string;
  invalidCredentials: string;
  passwordsDoNotMatch: string;
  passwordTooShort: string;
  accountCreatedSuccess: string;
  accountCreatedError: string;

  // ── Auth — Register ──
  createAccountTitle: string;
  registerSubtitle: string;
  fullName: string;
  yourName: string;
  confirmPassword: string;
  repeatPassword: string;
  alreadyHaveAccount: string;
  creatingAccount: string;
  termsAgreement: string;
  marketingConsent: string;
  minCharsPassword: string;
  cpf: string;
  invalidCpf: string;

  // ── Auth — Register (Cadastrar page) ──
  joinDQSAcademy: string;
  joinSubtitle: string;
  benefitHighQuality: string;
  benefitInternationalCerts: string;
  benefitExpertContent: string;
  benefitSupport: string;
  accountCreatedTitle: string;
  confirmEmailSent: string;
  checkSpam: string;
  resendEmail: string;
  resendingEmail: string;
  fillAllFields: string;
  fillYourData: string;
  emailResent: string;
  emailResendError: string;
  acceptTermsRequired: string;

  // ── Auth — Recovery ──
  recoverPassword: string;
  recoverPasswordSubtitle: string;
  sendRecoveryLink: string;
  sendingEmail: string;
  emailSent: string;
  recoverySentMessage: string;
  sendError: string;

  // ── Auth — Reset Password ──
  resetPassword: string;
  resetPasswordSubtitle: string;
  newPassword: string;
  confirmNewPassword: string;
  resetButton: string;
  resettingPassword: string;
  passwordResetSuccess: string;
  passwordResetSuccessMessage: string;
  passwordResetError: string;

  // ── Student Area ──
  studentArea: string;
  myCoursesTab: string;
  profileTab: string;
  certificatesTab: string;
  // Biblioteca interna (só colaborador @dqs.de — aba nem aparece pros outros)
  libraryTab: string;
  libraryInfoTitle: string;
  libraryInfoSubtitle: string;
  libraryInternalBadge: string;
  libraryEmpty: string;
  libraryEmptySubtitle: string;
  libraryOpenFile: string;
  libraryOpenLink: string;
  // Transparência de IA (EU AI Act, Art. 50) — texto tem de existir no idioma
  // do usuário, senão o aviso não cumpre a função de informar.
  aiDisclosureLesson: string;
  aiDisclosureDocument: string;
  aiDisclosurePreview: string;
  aiDisclosurePreviewNoVideo: string;
  aiDisclosureAriaLabel: string;
  hello: string;
  welcomeToLearning: string;

  // ── My Courses ──
  inProgress: string;
  completed: string;
  notStarted: string;
  continueWatching: string;
  startCourse: string;
  reviewCourse: string;
  enrolledCourses: string;
  yourCourses: string;
  noCoursesYet: string;
  noCoursesSubtitle: string;
  lessons: string;
  lastAccessed: string;
  today: string;
  yesterday: string;
  daysAgo: string;
  percentCompleted: string;

  // ── My Certificates ──
  downloadCertificate: string;
  viewCertificate: string;
  noCertificatesYet: string;
  noCertificatesSubtitle: string;
  certificatesInfoTitle: string;
  certificatesInfoSubtitle: string;
  issuedOn: string;
  verificationCode: string;
  downloadPdf: string;
  certificateValidation: string;
  certificateValidationDesc: string;

  // ── My Profile ──
  personalInfo: string;
  personalInfoSubtitle: string;
  professionalInfo: string;
  professionalInfoSubtitle: string;
  preferences: string;
  security: string;
  securitySubtitle: string;
  company: string;
  companyPlaceholder: string;
  position: string;
  positionPlaceholder: string;
  fieldOfWork: string;
  fieldOfWorkPlaceholder: string;
  areaOfInterest: string;
  selectArea: string;
  changePassword: string;
  changePasswordSubtitle: string;
  currentPassword: string;
  saveChanges: string;
  savingChanges: string;
  profileSaved: string;
  profileSaveError: string;
  passwordChanged: string;
  passwordChangeError: string;
  currentPasswordIncorrect: string;
  accountStatus: string;
  accountStatusSubtitle: string;
  registrationDate: string;
  accessType: string;
  status: string;
  active: string;
  roleStudent: string;
  roleAdmin: string;
  roleModerator: string;
  contactSupport: string;
  passwordDialogTitle: string;
  passwordDialogSubtitle: string;
  changingPassword: string;

  // Profile interest options
  interestQuality: string;
  interestEnvironment: string;
  interestSafety: string;
  interestIntegrated: string;
  interestInfoSec: string;
  interestAudit: string;
  interestOther: string;

  // ── Cursos (Catalog) ──
  catalogTitle: string;
  catalogSubtitle: string;
  searchCoursesPlaceholder: string;
  sortRelevance: string;
  sortPopular: string;
  sortTopRated: string;
  sortLowestPrice: string;
  sortHighestPrice: string;
  coursesFound: string;
  courseWord: string;
  coursesWord: string;
  noCoursesFound: string;
  noCoursesFoundSubtitle: string;
  loadError: string;
  continueCourse: string;
  viewCertificate: string;
  enrollErrorTitle: string;
  enrollErrorDescription: string;

  // ── Course Filters ──
  filters: string;
  clearFilters: string;
  filterCategory: string;
  filterStandard: string;

  // ── Course Detail ──
  backToCatalog: string;
  reviews: string;
  reviewsCount: string;
  evaluations: string;
  ofContent: string;
  lessonsCount: string;
  moduleWord: string;
  modulesWord: string;
  coursePreview: string;
  progress: string;
  accessCourse: string;
  buyNow: string;
  immediateAccess: string;
  certificateIncluded: string;
  courseIncludes: string;
  videoLessons: string;
  completionCertificate: string;
  oneYearAccess: string;
  whatYouWillLearn: string;
  courseContent: string;
  totalDuration: string;
  forWhomTitle: string;
  prerequisites: string;
  includedMaterials: string;
  readyToStart: string;
  readyToStartSubtitle: string;
  viewOtherCourses: string;
  paymentConfirmed: string;
  paymentError: string;
  noPaymentUrl: string;
  serverError: string;
  installmentsOf: string;
  videoType: string;

  // ── Checkout ──
  payment: string;
  cardTab: string;
  pixTab: string;
  boletoTab: string;
  cardNumber: string;
  cardName: string;
  cardNamePlaceholder: string;
  expiry: string;
  cvv: string;
  installments: string;
  installmentSingle: string;
  installmentMultiple: string;
  finishPayment: string;
  processingPayment: string;
  originalPrice: string;
  securePurchase: string;
  sslCertificate: string;
  sevenDayGuarantee: string;
  scanQrCode: string;
  codeExpiresIn: string;
  copyPixKey: string;
  boletoExpiresIn: string;
  generateBoleto: string;
  boletoConfirmation: string;
  comingSoon: string;
  comingSoonBadge: string;

  // ── Checkout Success ──
  paymentConfirmedTitle: string;
  paymentProcessingSubtitle: string;
  goToMyCourses: string;
  validationFailed: string;
  processing: string;
  startCourseNow: string;
  whatHappensNext: string;
  nextStepAccessTitle: string;
  nextStepAccessDesc: string;
  nextStepEmailTitle: string;
  nextStepEmailDesc: string;
  nextStepStudyTitle: string;
  nextStepStudyDesc: string;
  nextStepCertTitle: string;
  nextStepCertDesc: string;
  checkoutSuccessSupport: string;
  viewCourse: string;

  // ── FAQ ──
  faqTitle: string;
  faqSubtitle: string;
  faqCategoryCoursesTitle: string;
  faqCategoryCertificatesTitle: string;
  faqCategoryPaymentTitle: string;
  faqCategoryCompaniesTitle: string;
  faqNotFoundTitle: string;
  faqNotFoundSubtitle: string;
  faqContactButton: string;
  faqExploreButton: string;
  // FAQ — Courses
  faqCourseQ1: string; faqCourseA1: string;
  faqCourseQ2: string; faqCourseA2: string;
  faqCourseQ3: string; faqCourseA3: string;
  faqCourseQ4: string; faqCourseA4: string;
  // FAQ — Certificates
  faqCertQ1: string; faqCertA1: string;
  faqCertQ2: string; faqCertA2: string;
  faqCertQ3: string; faqCertA3: string;
  // FAQ — Payment
  faqPayQ1: string; faqPayA1: string;
  faqPayQ2: string; faqPayA2: string;
  faqPayQ3: string; faqPayA3: string;
  faqPayQ4: string; faqPayA4: string;
  // FAQ — Companies
  faqCompQ1: string; faqCompA1: string;
  faqCompQ2: string; faqCompA2: string;
  faqCompQ3: string; faqCompA3: string;
  faqCompQ4: string; faqCompA4: string;

  // ── About ──
  aboutHeroTitle: string;
  aboutHeroSubtitle: string;
  whoWeAre: string;
  whoWeAreP1: string;
  whoWeAreP2: string;
  whoWeAreP3: string;
  yearsGlobalExperience: string;
  partOfGroup: string;
  partOfGroupSubtitle: string;
  countries: string;
  clients: string;
  auditors: string;
  standards: string;
  ourValues: string;
  ourValuesSubtitle: string;
  valueExcellence: string;
  valueExcellenceDesc: string;
  valuePartnership: string;
  valuePartnershipDesc: string;
  valueKnowledge: string;
  valueKnowledgeDesc: string;
  valueGlobalization: string;
  valueGlobalizationDesc: string;
  ourTrajectory: string;
  milestone1985: string;
  milestone1990: string;
  milestone1994: string;
  milestone2010: string;
  milestone2025: string;
  milestoneToday: string;
  milestoneLabel: string;
  educationalDifferential: string;
  educationalDifferentialP1: string;
  educationalDifferentialP2: string;
  whyDQSAcademy: string;
  diffContent: string;
  diffMethodology: string;
  diffCertificates: string;
  diffSupport: string;
  diffUpdated: string;
  aboutCtaTitle: string;
  aboutCtaSubtitle: string;

  // ── Para Empresas ──
  companiesHeroBadge: string;
  companiesHeroTitle: string;
  companiesHeroSubtitle: string;
  requestContact: string;
  useCasesTitle: string;
  useCasesSubtitle: string;
  useCaseCertTitle: string;
  useCaseCertDesc: string;
  useCaseDevTitle: string;
  useCaseDevDesc: string;
  useCaseComplianceTitle: string;
  useCaseComplianceDesc: string;
  useCaseOnboardingTitle: string;
  useCaseOnboardingDesc: string;
  differentialsTitle: string;
  differentialsSubtitle: string;
  diffGlobalExpertise: string;
  diffGlobalExpertiseDesc: string;
  diffUpdatedContent: string;
  diffUpdatedContentDesc: string;
  diffPracticalMethod: string;
  diffPracticalMethodDesc: string;
  diffB2B: string;
  diffB2BDesc: string;
  diffDedicatedSupport: string;
  diffDedicatedSupportDesc: string;
  diffRecognizedCert: string;
  diffRecognizedCertDesc: string;
  companiesCtaTitle: string;
  companiesCtaSubtitle: string;
  talkToSpecialist: string;
  viewCompleteSolutions: string;

  // ── Soluções Corporativas ──
  corpHeroTitle: string;
  corpHeroSubtitle: string;
  talkToConsultantBtn: string;
  knowCourses: string;
  corpBenefitsTitle: string;
  corpBenefitsSubtitle: string;
  corpBenefitTeamTitle: string;
  corpBenefitTeamDesc: string;
  corpBenefitReportsTitle: string;
  corpBenefitReportsDesc: string;
  corpBenefitTrailsTitle: string;
  corpBenefitTrailsDesc: string;
  corpBenefitCertsTitle: string;
  corpBenefitCertsDesc: string;
  corpHowItWorks: string;
  corpHowItWorksSubtitle: string;
  corpStep1Title: string;
  corpStep1Desc: string;
  corpStep2Title: string;
  corpStep2Desc: string;
  corpStep3Title: string;
  corpStep3Desc: string;
  corpStep4Title: string;
  corpStep4Desc: string;
  corpMetricCompanies: string;
  corpMetricProfessionals: string;
  corpMetricSatisfaction: string;
  corpMetricExperience: string;
  corpFeaturesTitle: string;
  corpFeature1: string;
  corpFeature1Desc: string;
  corpFeature2: string;
  corpFeature3: string;
  corpFeature4: string;
  corpFeature5: string;
  corpFeature6: string;
  corpReadyToStart: string;
  corpReadySubtitle: string;
  requestProposal: string;
  corpServiceBrazil: string;

  // ── Verificar Certificado ──
  certVerificationTitle: string;
  certVerificationSubtitle: string;
  verifyingCert: string;
  certValid: string;
  certValidSubtitle: string;
  certNotFound: string;
  certNotFoundSubtitle: string;
  certStudentLabel: string;
  certCourseLabel: string;
  certIssueDateLabel: string;
  certCodeLabel: string;
  backToSite: string;

  // ── Not Found ──
  notFoundTitle: string;
  notFoundSubtitle: string;
  notFoundDescription: string;
  returnToHome: string;
  exploreCatalog: string;

  // ── Cart Dropdown ──
  yourCart: string;
  continueShopping: string;
  courseWordSingular: string;
  courseWordPlural: string;

  // ── Search Dialog ──
  searchCoursesLabel: string;
  noResultsFor: string;
  resultsCount: string;
  resultWord: string;
  resultsWord: string;

  // ── Reviews ──
  reviewsTitle: string;
  reviewWord: string;
  reviewsWordPlural: string;
  noReviewsYet: string;
  rateCourse: string;
  rateModalTitle: string;
  clickToRate: string;
  starsOf: string;
  leaveComment: string;
  submitReview: string;
  submittingReview: string;
  reviewSuccess: string;
  reviewError: string;
  selectRating: string;
  studentDefault: string;

  // ── Language selector ──
  portuguese: string;
  english: string;
  spanish: string;

  // ── Contact Page ──
  contactPageTitle: string;
  contactPageSubtitle: string;
  contactName: string;
  contactCompany: string;
  contactRole: string;
  contactEmployees: string;
  contactCoursesInterest: string;
  contactOtherCourseLabel: string;
  contactOtherCoursePlaceholder: string;
  contactEmail: string;
  contactPhone: string;
  contactMessage: string;
  contactSubmit: string;
  contactSubmitting: string;
  contactSuccess: string;
  contactSuccessMessage: string;
  contactError: string;

  // ── Lesson Player (chrome) ──
  lessonBack: string;
  videoLesson: string;
  pdfMaterial: string;
  quizType: string;
  closeContent: string;
  accessContent: string;
  lessonCompletedToast: string;
  lessonCompleteError: string;
  certificateGeneratedToast: string;
  certificateRequestError: string;
  concludeCourseError: string;
  noCourseAccessTitle: string;
  noCourseAccessDescription: string;
  previousLesson: string;
  nextLabel: string;
  markAsCompleted: string;
  lessonCompletedBadge: string;
  completeQuizToAdvance: string;
  finishCourse: string;
  congratsCourseCompleted: string;

  // ── Lesson Blocks — shared ──
  howToNavigate: string;
  attentionPoint: string;
  contentInProduction: string;
  watchVideo: string;
  ofConnector: string;
  youGotCorrect: string;
  questionsWord: string;
  classificationsWord: string;
  matchesWord: string;
  nextLesson: string;
  scoreLabel: string;
  retakeQuiz: string;
  tryAgain: string;

  // ── Quiz Block ──
  quizDefaultTitle: string;
  activityCompleted: string;
  keepPracticing: string;
  excellentWork: string;
  goodWorkReviewScenarios: string;
  goodWorkReviewItems: string;
  reviewConceptsTryAgain: string;
  passedCongrats: string;
  almostThere: string;
  youNeed: string;
  toPass: string;
  youScored: string;
  generatingCertificate: string;
  certificateBeingGenerated: string;
  confirmAnswer: string;
  nextQuestion: string;
  viewResult: string;
  questionLabel: string;
  minimumScore: string;
  correctLabel: string;
  incorrectLabel: string;
  correctAnswerIs: string;

  // ── Classification Quiz Block ──
  classifyContext: string;
  yourAnswer: string;
  correctColon: string;
  scenarioLabel: string;
  eventLabel: string;
  confirmLabel: string;
  nextItemLabel: string;

  // ── Auditor Scenario Block ──
  hideAnalysis: string;
  revealAnalysis: string;
  scenarioAnalysisLabel: string;
  scenarioAnalysisTitle: string;
  internalLabel: string;
  externalLabel: string;
  selectRatingStars: string;
  ratingSubmittedToast: string;
  submitRatingError: string;
  courseCompletedSuccess: string;
  stepCompleted: string;
  answerCorrectStepDone: string;
  advanceNextLesson: string;
  reviewQuiz: string;
  whatDidYouThink: string;
  ratingHelperText: string;
  outOfFiveStars: string;
  commentPlaceholder: string;
  submitRating: string;
  thanksForRating: string;
  correctOption: string;
  incorrectAnswerTryAgain: string;

  // ── Checklist Block ──
  auditChecklistTitle: string;
  verifiedCount: string;
  checklistHelper: string;
  verifyLabel: string;
  uncheckLabel: string;
  evidenceToRequest: string;
  checklistCompleteTitle: string;
  checklistCompleteMessage: string;

  // ── Matching Block ──
  termsLabel: string;
  definitionsLabel: string;
  dragDefinitionHere: string;
  allDefinitionsMatched: string;
  perfectAllCorrect: string;
  verifyAnswers: string;

  // ── Scenario Block(s) ──
  situationLabel: string;
  questionWordLabel: string;
  viewAnswer: string;
  hideAnswer: string;
  answerLabel: string;
  expertTip: string;
  scenarioWord: string;

  // ── PDF / Flip / Media Blocks ──
  downloadPdfMaterial: string;
  clickToFlip: string;
  definitionLabel: string;
  materialInProduction: string;
  openMaterial: string;

  // ── Sidebar ──
  collapseLabel: string;
  expandLabel: string;
  moduleLockedTooltip: string;
  lockedLabel: string;
  completedFemPlural: string;
  unlockModuleTooltip: string;

  // ── Block Error Boundary ──
  blockRenderErrorTitle: string;
  blockRenderErrorMessage: string;
}

export type Language = 'pt-BR' | 'en' | 'es';

export const translations: Record<Language, Translations> = {
  'pt-BR': {
    // Navigation
    home: 'Início',
    courses: 'Cursos',
    categories: 'Categorias',
    forCompanies: 'Para Empresas',
    about: 'Sobre',
    login: 'Entrar',
    register: 'Cadastrar',
    logout: 'Sair',
    myCourses: 'Meus Cursos',
    myProfile: 'Meu Perfil',
    helpAndSupport: 'Ajuda e Suporte',
    certificates: 'Certificados',
    search: 'Buscar',
    searchPlaceholder: 'Buscar cursos...',
    faq: 'Perguntas Frequentes',

    // Categories
    quality: 'Qualidade',
    environment: 'Meio Ambiente',
    workSafety: 'Segurança do Trabalho',
    infoSecurity: 'Segurança da Informação',
    automotive: 'Automotivo',
    foodSafety: 'Segurança de Alimentos',
    technology: 'Tecnologia',
    businessContinuity: 'Continuidade de Negócios',
    compliance: 'Compliance',

    // Common
    viewDetails: 'Ver detalhes',
    enrollNow: 'Inscreva-se agora',
    startNow: 'Começar agora',
    learnMore: 'Saiba mais',
    cart: 'Carrinho',
    emptyCart: 'Seu carrinho está vazio',
    goToCheckout: 'Ir para pagamento',
    searchCourses: 'Buscar cursos',
    remove: 'Remover',
    total: 'Total',
    backToHome: 'Voltar para o início',
    backToLogin: 'Voltar para login',
    backToCourses: 'Voltar para Cursos',
    backToCourse: 'Voltar para o curso',
    free: 'Grátis',
    loading: 'Carregando...',
    saving: 'Salvando...',
    save: 'Salvar',
    cancel: 'Cancelar',
    close: 'Fechar',
    or: 'ou',
    and: 'e',
    yes: 'Sim',
    no: 'Não',

    // Category Pages
    categoryTitle: 'Categoria',
    categoryDescription: 'Explore nossos cursos de',
    noCourses: 'Nenhum curso encontrado nesta categoria.',
    categoryNotFound: 'Categoria não encontrada',
    viewAllCategories: 'Ver todas as categorias',
    coursesIn: 'Cursos de',
    comingSoonCategory: 'Em breve teremos cursos nesta categoria.',
    exploreOtherCourses: 'Explorar outros cursos',
    viewCourses: 'Ver cursos',

    // Categories Index
    categoriesHeroTitle: 'Categorias',
    categoriesHeroSubtitle: 'Explore nossos cursos organizados por área de conhecimento. Encontre treinamentos especializados para cada necessidade da sua organização.',
    categoriesNotFoundTitle: 'Não encontrou o que procura?',
    categoriesNotFoundSubtitle: 'Explore nosso catálogo completo com filtros avançados ou entre em contato para soluções personalizadas.',
    exploreAllCourses: 'Explorar Todos os Cursos',

    // Hero Section
    heroBadge: 'Líder mundial em certificação de sistemas de gestão',
    heroTitle1: 'Desenvolva suas competências em',
    heroTitle2: 'Sistemas de Gestão',
    heroSubtitle: 'Cursos online desenvolvidos por especialistas da DQS, com metodologia prática e certificados reconhecidos pelo mercado.',
    exploreCourses: 'Explorar Cursos',
    corporateSolutions: 'Soluções Corporativas',
    availableCourses: 'Cursos Disponíveis',
    coursesCompleted: 'Cursos Realizados',
    trainedProfessionals: 'Profissionais Capacitados',
    yearsExperience: 'Anos de Experiência',

    // Featured Courses
    featuredCourses: 'Cursos em Destaque',
    featuredCoursesSubtitle: 'Os cursos mais procurados por profissionais e empresas',
    viewAllCourses: 'Ver todos os cursos',
    bestSeller: 'Mais Vendido',
    newCourse: 'Novo',
    hours: 'horas',
    students: 'alunos',

    // Why DQS
    whyDQS: 'Por que escolher a DQS Academy?',
    whyDQSSubtitle: 'A credibilidade de uma das maiores certificadoras do mundo, agora em uma plataforma de educação corporativa.',
    globalExpertise: 'Expertise Global',
    globalExpertiseDesc: 'Mais de 35 anos de experiência em certificação de sistemas de gestão em todo o mundo.',
    specializedInstructors: 'Instrutores Especializados',
    specializedInstructorsDesc: 'Auditores e consultores com experiência prática em implementação de normas.',
    recognizedCertificates: 'Certificados Reconhecidos',
    recognizedCertificatesDesc: 'Certificados com validade nacional emitidos pela DQS Academy Brasil.',
    practicalMethodology: 'Metodologia Prática',
    practicalMethodologyDesc: 'Conteúdo aplicável com estudos de caso e exercícios do mundo real.',
    continuousSupport: 'Suporte Contínuo',
    continuousSupportDesc: 'Acesso a materiais complementares e suporte técnico especializado.',
    professionalCommunity: 'Comunidade de Profissionais',
    professionalCommunityDesc: 'Networking com milhares de profissionais da área de sistemas de gestão.',
    alignedWithStandards: 'Cursos alinhados às principais normas internacionais',
    certified: 'Certificados',

    // Categories Section
    exploreByCategory: 'Explore por Categoria',
    exploreByCategorySubtitle: 'Encontre cursos especializados em diversas áreas de sistemas de gestão',
    coursesCount: 'cursos',
    categorySectionQualityDesc: 'ISO 9001, Gestão da Qualidade Total, Ferramentas da Qualidade',
    categorySectionEnvironmentDesc: 'ISO 14001, Gestão Ambiental, Sustentabilidade Corporativa',
    categorySectionSafetyDesc: 'ISO 45001, SST, Prevenção de Acidentes',
    categorySectionInfoSecDesc: 'ISO 27001, LGPD, Cibersegurança',
    categorySectionAutomotiveDesc: 'IATF 16949, VDA, Core Tools',
    categorySectionFoodDesc: 'ISO 22000, FSSC, HACCP',

    // Corporate Section
    corporateSolutionsBadge: 'Soluções Corporativas',
    corporateTitle: 'Capacite sua equipe com a expertise DQS',
    corporateSubtitle: 'Ofereça treinamentos de alta qualidade para sua organização. Gerencie matrículas, acompanhe o progresso e desenvolva competências em sistemas de gestão.',
    teamManagement: 'Gestão de Equipes',
    teamManagementDesc: 'Atribua cursos e acompanhe o progresso de todos os colaboradores.',
    detailedReports: 'Relatórios Detalhados',
    detailedReportsDesc: 'Dashboards com métricas de engajamento e conclusão por equipe.',
    corporateCertificates: 'Certificados Corporativos',
    corporateCertificatesDesc: 'Certificados personalizados com a identidade da sua empresa.',
    learnSolutions: 'Conheça as Soluções',
    talkToConsultant: 'Fale com um Consultor',
    companiesServed: 'Empresas Atendidas',
    employeesTrained: 'Colaboradores Treinados',
    satisfaction: 'Satisfação',
    platformAccess: 'Acesso à Plataforma',
    exclusivePortal: 'Portal Exclusivo',
    realTimeReports: 'Relatórios em Tempo Real',

    // CTA Section
    startYourJourney: 'Comece sua jornada hoje',
    readyToDevelop: 'Pronto para desenvolver suas competências?',
    ctaSubtitle: 'Junte-se a milhares de profissionais que já avançaram em suas carreiras com os cursos da DQS Academy Brasil.',
    createFreeAccount: 'Criar Conta Gratuita',
    freeSignup: 'Cadastro gratuito • Acesso imediato • Sem compromisso',

    // Footer
    footerTagline: 'Excelência em Capacitação',
    footerDescription: 'Desenvolvendo competências em sistemas de gestão com a credibilidade e expertise da DQS, líder mundial em certificação.',
    coursesTitle: 'Cursos',
    categoriesTitle: 'Categorias',
    institutionalTitle: 'Institucional',
    legalTitle: 'Legal',
    aboutDQS: 'Sobre a DQS Academy',
    contact: 'Contato',
    termsOfUse: 'Termos de Uso',
    privacyPolicy: 'Política de Privacidade',
    cookiePolicy: 'Política de Cookies',
    allRightsReserved: 'Todos os direitos reservados.',
    companyGroup: 'Uma empresa do grupo',

    // Auth — Login
    welcomeBack: 'Bem-vindo de volta',
    loginSubtitle: 'Entre na sua conta para continuar',
    email: 'E-mail',
    password: 'Senha',
    forgotPassword: 'Esqueceu a senha?',
    noAccount: 'Não tem conta?',
    createAccount: 'Criar conta',
    loginTab: 'Entrar',
    createAccountTab: 'Criar Conta',
    loggingIn: 'Entrando...',
    loginButton: 'Entrar',
    orContinueWith: 'ou continue com',
    invalidCredentials: 'Email ou senha inválidos',
    passwordsDoNotMatch: 'As senhas não coincidem',
    passwordTooShort: 'A senha deve conter pelo menos 8 caracteres, incluindo letras maiúsculas, minúsculas e números.',
    accountCreatedSuccess: 'Conta criada! Verifique seu email para confirmar o cadastro.',
    accountCreatedError: 'Erro ao criar conta. Tente novamente.',

    // Auth — Register
    createAccountTitle: 'Criar sua conta',
    registerSubtitle: 'Cadastre-se para acessar nossos cursos',
    fullName: 'Nome completo',
    yourName: 'Seu nome',
    confirmPassword: 'Confirmar senha',
    repeatPassword: 'Repita a senha',
    alreadyHaveAccount: 'Já tem uma conta?',
    creatingAccount: 'Criando conta...',
    termsAgreement: 'Li e aceito os',
    marketingConsent: 'Desejo receber novidades e ofertas por e-mail',
    minCharsPassword: 'Mínimo 6 caracteres',
    cpf: 'CPF',
    invalidCpf: 'CPF inválido',

    // Auth — Register (Cadastrar page)
    joinDQSAcademy: 'Junte-se à DQS Academy',
    joinSubtitle: 'Crie sua conta e tenha acesso aos melhores cursos em sistemas de gestão desenvolvidos por especialistas da DQS.',
    benefitHighQuality: 'Acesso a cursos de alta qualidade',
    benefitInternationalCerts: 'Certificados reconhecidos internacionalmente',
    benefitExpertContent: 'Conteúdo desenvolvido por especialistas DQS',
    benefitSupport: 'Suporte técnico e pedagógico',
    accountCreatedTitle: 'Conta criada com sucesso!',
    confirmEmailSent: 'Enviamos um e-mail de confirmação para',
    checkSpam: 'Não recebeu? Verifique a pasta de spam.',
    resendEmail: 'Reenviar e-mail de confirmação',
    resendingEmail: 'Reenviando...',
    fillAllFields: 'Por favor, preencha todos os campos.',
    fillYourData: 'Preencha seus dados para começar',
    emailResent: 'E-mail reenviado!',
    emailResendError: 'Erro ao reenviar',
    acceptTermsRequired: 'Você precisa aceitar os termos de uso para continuar.',

    // Auth — Recovery
    recoverPassword: 'Recuperar senha',
    recoverPasswordSubtitle: 'Informe seu email e enviaremos um link para redefinir sua senha.',
    sendRecoveryLink: 'Enviar link de recuperação',
    sendingEmail: 'Enviando...',
    emailSent: 'Email enviado!',
    recoverySentMessage: 'Enviamos um link de recuperação para',
    sendError: 'Erro ao enviar email',

    // Auth — Reset Password
    resetPassword: 'Redefinir senha',
    resetPasswordSubtitle: 'Escolha uma nova senha para sua conta.',
    newPassword: 'Nova senha',
    confirmNewPassword: 'Confirmar nova senha',
    resetButton: 'Redefinir senha',
    resettingPassword: 'Salvando...',
    passwordResetSuccess: 'Senha redefinida!',
    passwordResetSuccessMessage: 'Sua senha foi alterada com sucesso. Redirecionando para o login...',
    passwordResetError: 'Erro ao redefinir senha',

    // Student Area
    studentArea: 'Área do Aluno',
    myCoursesTab: 'Meus Cursos',
    profileTab: 'Meu Perfil',
    certificatesTab: 'Certificados',
    libraryTab: 'Biblioteca',
    libraryInfoTitle: 'Materiais de apoio',
    libraryInfoSubtitle: 'Apostilas, ebooks e slides dos treinamentos internos, para você consultar quando precisar.',
    libraryInternalBadge: 'Uso interno DQS',
    libraryEmpty: 'Nenhum material publicado ainda',
    libraryEmptySubtitle: 'Assim que os materiais dos treinamentos forem publicados, eles aparecem aqui.',
    libraryOpenFile: 'Baixar',
    libraryOpenLink: 'Abrir',
    aiDisclosureLesson: 'Imagem e voz do apresentador geradas por IA. Conteúdo técnico criado e revisado por especialistas DQS na norma deste curso.',
    aiDisclosureDocument: 'Material produzido com apoio de IA e revisado por especialistas DQS na norma deste curso.',
    aiDisclosurePreview: 'Vídeos com imagem e voz geradas por IA. Materiais produzidos com apoio de IA. Todo o conteúdo técnico é criado e revisado por especialistas DQS na norma do curso.',
    aiDisclosurePreviewNoVideo: 'Materiais produzidos com apoio de IA. Todo o conteúdo técnico é criado e revisado por especialistas DQS na norma do curso.',
    aiDisclosureAriaLabel: 'Aviso sobre uso de inteligência artificial',
    hello: 'Olá',
    welcomeToLearning: 'Bem-vindo à sua área de aprendizado',

    // My Courses
    inProgress: 'Em andamento',
    completed: 'Concluído',
    notStarted: 'Não iniciado',
    continueWatching: 'Continuar',
    startCourse: 'Iniciar',
    reviewCourse: 'Revisar Curso',
    enrolledCourses: 'Cursos matriculados',
    yourCourses: 'Seus cursos',
    noCoursesYet: 'Você ainda não está matriculado em nenhum curso.',
    noCoursesSubtitle: 'Explore nosso catálogo e comece a aprender.',
    lessons: 'aulas',
    lastAccessed: 'Último acesso:',
    today: 'Hoje',
    yesterday: 'Ontem',
    daysAgo: 'dias atrás',
    percentCompleted: 'concluído',

    // My Certificates
    downloadCertificate: 'Baixar PDF',
    viewCertificate: 'Visualizar',
    noCertificatesYet: 'Nenhum certificado ainda',
    noCertificatesSubtitle: 'Conclua um curso para receber seu certificado.',
    certificatesInfoTitle: 'Certificados DQS Academy',
    certificatesInfoSubtitle: 'Os certificados são emitidos automaticamente após a conclusão de 100% do curso. Cada certificado possui um código único de validação que pode ser verificado em nosso portal.',
    issuedOn: 'Emitido em:',
    verificationCode: 'Código:',
    downloadPdf: 'Baixar PDF',
    certificateValidation: 'Validação de Certificados',
    certificateValidationDesc: 'Todos os certificados emitidos pela DQS Academy Brasil podem ser validados através do código único presente no documento.',

    // My Profile
    personalInfo: 'Informações Pessoais',
    personalInfoSubtitle: 'Seus dados cadastrais básicos',
    professionalInfo: 'Informações Profissionais',
    professionalInfoSubtitle: 'Dados da sua empresa e cargo (opcional)',
    preferences: 'Preferências',
    security: 'Segurança',
    securitySubtitle: 'Gerencie sua senha e segurança da conta',
    company: 'Empresa',
    companyPlaceholder: 'Nome da empresa',
    position: 'Cargo',
    positionPlaceholder: 'Seu cargo atual',
    fieldOfWork: 'Área de atuação',
    fieldOfWorkPlaceholder: 'Ex: Gestão da Qualidade',
    areaOfInterest: 'Área de interesse principal',
    selectArea: 'Selecione uma área',
    changePassword: 'Alterar senha',
    changePasswordSubtitle: 'Altere sua senha de acesso',
    currentPassword: 'Senha atual',
    saveChanges: 'Salvar alterações',
    savingChanges: 'Salvando...',
    profileSaved: 'Perfil atualizado com sucesso!',
    profileSaveError: 'Erro ao salvar',
    passwordChanged: 'Senha alterada com sucesso!',
    passwordChangeError: 'Erro ao alterar senha',
    currentPasswordIncorrect: 'Senha atual incorreta',
    accountStatus: 'Status da Conta',
    accountStatusSubtitle: 'Informações sobre sua conta',
    registrationDate: 'Data de cadastro',
    accessType: 'Tipo de acesso',
    status: 'Status',
    active: 'Ativo',
    roleStudent: 'Aluno',
    roleAdmin: 'Administrador',
    roleModerator: 'Moderador',
    contactSupport: 'Entre em contato com o suporte se necessário',
    passwordDialogTitle: 'Alterar senha',
    passwordDialogSubtitle: 'Informe sua senha atual e a nova senha desejada.',
    changingPassword: 'Alterando...',

    interestQuality: 'Qualidade (ISO 9001)',
    interestEnvironment: 'Meio Ambiente (ISO 14001)',
    interestSafety: 'Saúde e Segurança (ISO 45001)',
    interestIntegrated: 'Sistema Integrado de Gestão',
    interestInfoSec: 'Segurança da Informação (ISO 27001)',
    interestAudit: 'Auditoria',
    interestOther: 'Outros',

    // Cursos (Catalog)
    catalogTitle: 'Catálogo de Cursos',
    catalogSubtitle: 'Explore nossa seleção de cursos em sistemas de gestão, desenvolvidos por especialistas da DQS com metodologia prática e certificados reconhecidos.',
    searchCoursesPlaceholder: 'Buscar cursos...',
    sortRelevance: 'Relevância',
    sortPopular: 'Mais Populares',
    sortTopRated: 'Melhor Avaliados',
    sortLowestPrice: 'Menor Preço',
    sortHighestPrice: 'Maior Preço',
    coursesFound: 'curso',
    courseWord: 'curso',
    coursesWord: 'cursos',
    noCoursesFound: 'Nenhum curso encontrado',
    noCoursesFoundSubtitle: 'Tente ajustar os filtros ou fazer uma nova busca.',
    loadError: 'Erro ao carregar cursos. Tente novamente mais tarde.',
    continueCourse: 'Continuar curso',
    viewCertificate: 'Ver Certificado',
    enrollErrorTitle: 'Erro ao iniciar curso',
    enrollErrorDescription: 'Tente novamente.',

    // Course Filters
    filters: 'Filtros',
    clearFilters: 'Limpar',
    filterCategory: 'Categoria',
    filterStandard: 'Norma / Padrão',

    // Course Detail
    backToCatalog: 'Voltar para Cursos',
    reviews: 'avaliações',
    reviewsCount: 'avaliações',
    evaluations: 'avaliações',
    ofContent: 'de conteúdo',
    lessonsCount: 'aulas',
    moduleWord: 'módulo',
    modulesWord: 'módulos',
    coursePreview: 'Prévia do curso',
    progress: 'Progresso',
    accessCourse: 'Acessar curso',
    buyNow: 'Comprar Agora',
    immediateAccess: 'Acesso imediato',
    certificateIncluded: 'Certificado incluso',
    courseIncludes: 'Este curso inclui:',
    videoLessons: 'aulas em vídeo',
    completionCertificate: 'Certificado de conclusão',
    oneYearAccess: '1 ano de acesso',
    whatYouWillLearn: 'O que você vai aprender',
    courseContent: 'Conteúdo do curso',
    totalDuration: 'de duração total',
    forWhomTitle: 'Para quem é este curso',
    prerequisites: 'Pré-requisitos',
    includedMaterials: 'Materiais inclusos',
    readyToStart: 'Pronto para começar sua jornada?',
    readyToStartSubtitle: 'Invista no seu desenvolvimento profissional e destaque-se no mercado.',
    viewOtherCourses: 'Ver outros cursos',
    paymentConfirmed: 'Pagamento confirmado! Bem-vindo ao curso 🎉',
    paymentError: 'Erro ao iniciar o pagamento. Tente novamente.',
    noPaymentUrl: 'Nenhuma URL de pagamento retornada. Verifique a configuração do Stripe.',
    serverError: 'Erro ao conectar com o servidor de pagamentos.',
    installmentsOf: 'ou em até',
    videoType: 'Vídeo',

    // Checkout
    payment: 'Pagamento',
    cardTab: 'Cartão',
    pixTab: 'PIX',
    boletoTab: 'Boleto',
    cardNumber: 'Número do cartão',
    cardName: 'Nome no cartão',
    cardNamePlaceholder: 'Como está impresso no cartão',
    expiry: 'Validade',
    cvv: 'CVV',
    installments: 'Parcelas',
    installmentSingle: 'à vista',
    installmentMultiple: 'x de R$',
    finishPayment: 'Finalizar pagamento',
    processingPayment: 'Processando pagamento...',
    originalPrice: 'Preço original',
    securePurchase: 'Compra segura',
    sslCertificate: 'Certificado SSL',
    sevenDayGuarantee: '7 dias de garantia',
    scanQrCode: 'Escaneie o QR Code com seu app de banco',
    codeExpiresIn: 'Este código expira em',
    copyPixKey: 'Copiar chave PIX',
    boletoExpiresIn: 'O boleto vence em',
    generateBoleto: 'Gerar boleto',
    boletoConfirmation: 'Após o pagamento, a confirmação pode levar até 3 dias úteis.',
    comingSoon: 'Em breve! Integração com Stripe em andamento.',
    comingSoonBadge: 'Em breve',

    // Checkout Success
    paymentConfirmedTitle: 'Pagamento confirmado!',
    paymentProcessingSubtitle: 'Sua matrícula está sendo processada. Em instantes você terá acesso ao curso.',
    goToMyCourses: 'Ir para Meus Cursos',
    validationFailed: 'Revise o formulário',
    processing: 'Processando...',
    startCourseNow: 'Começar o curso agora',
    whatHappensNext: 'O que acontece agora?',
    nextStepAccessTitle: 'Acesso liberado imediatamente',
    nextStepAccessDesc: 'Sua matrícula já está ativa. Você pode começar a estudar agora mesmo.',
    nextStepEmailTitle: 'Email de confirmação enviado',
    nextStepEmailDesc: 'Em alguns minutos você recebe a nota fiscal e o link de acesso no seu email.',
    nextStepStudyTitle: 'Estude no seu ritmo',
    nextStepStudyDesc: 'Acesso vitalício. Pause e retome quando quiser, em qualquer dispositivo.',
    nextStepCertTitle: 'Certificado automático',
    nextStepCertDesc: 'Ao concluir 100% das aulas, o certificado fica disponível na sua área do aluno.',
    checkoutSuccessSupport: 'Alguma dúvida?',
    viewCourse: 'Ver o curso',

    // FAQ
    faqTitle: 'Perguntas Frequentes',
    faqSubtitle: 'Encontre respostas para as dúvidas mais comuns sobre nossos cursos, certificados, pagamentos e soluções corporativas.',
    faqCategoryCoursesTitle: 'Cursos',
    faqCategoryCertificatesTitle: 'Certificados',
    faqCategoryPaymentTitle: 'Pagamento',
    faqCategoryCompaniesTitle: 'Empresas',
    faqNotFoundTitle: 'Não encontrou sua resposta?',
    faqNotFoundSubtitle: 'Nossa equipe está pronta para ajudar. Entre em contato conosco e teremos prazer em esclarecer suas dúvidas.',
    faqContactButton: 'Falar com Atendimento',
    faqExploreButton: 'Explorar Cursos',
    faqCourseQ1: 'Quais tipos de cursos a DQS Academy oferece?',
    faqCourseA1: 'Oferecemos cursos em diversas áreas de sistemas de gestão, incluindo Qualidade (ISO 9001), Meio Ambiente (ISO 14001), Segurança do Trabalho (ISO 45001), Segurança da Informação (ISO 27001) e Automotivo (IATF 16949). Os cursos estão disponíveis nos formatos EAD, ao vivo e híbrido.',
    faqCourseQ2: 'Qual a duração média dos cursos?',
    faqCourseA2: 'A duração varia conforme o curso e nível de profundidade. Cursos introdutórios geralmente têm entre 8 e 16 horas, enquanto formações mais completas podem chegar a 32 horas ou mais. A carga horária está sempre indicada na página de cada curso.',
    faqCourseQ3: 'Os cursos são atualizados?',
    faqCourseA3: 'Sim, todos os nossos cursos são desenvolvidos e revisados por auditores e especialistas da DQS, garantindo que o conteúdo esteja sempre alinhado com as versões mais recentes das normas e melhores práticas de mercado.',
    faqCourseQ4: 'Posso acessar o conteúdo após a conclusão?',
    faqCourseA4: 'Sim, você mantém acesso ao conteúdo do curso por 12 meses após a matrícula. Isso permite revisar o material sempre que necessário.',
    faqCertQ1: 'Como funciona a emissão do certificado?',
    faqCertA1: 'Ao concluir 100% das aulas e atingir a nota mínima nas avaliações (quando aplicável), o certificado é emitido automaticamente e fica disponível para download na sua área do aluno.',
    faqCertQ2: 'O certificado tem validade internacional?',
    faqCertA2: 'Sim, os certificados da DQS Academy são reconhecidos internacionalmente, pois carregam a chancela da DQS, uma das certificadoras mais respeitadas do mundo com presença em mais de 60 países.',
    faqCertQ3: 'O certificado é válido para comprovação profissional?',
    faqCertA3: 'Sim, nossos certificados são válidos para comprovar capacitação profissional e podem ser utilizados em processos seletivos, auditorias e para atendimento a requisitos de competência.',
    faqPayQ1: 'Quais formas de pagamento são aceitas?',
    faqPayA1: 'Aceitamos cartão de crédito (até 12x), boleto bancário e PIX. Para compras corporativas, também oferecemos faturamento direto.',
    faqPayQ2: 'Posso solicitar reembolso?',
    faqPayA2: 'Sim, oferecemos garantia de 7 dias. Se você não estiver satisfeito com o curso, pode solicitar o reembolso integral dentro desse prazo, desde que não tenha concluído mais de 30% do conteúdo.',
    faqPayQ3: 'Existe desconto para compra de múltiplos cursos?',
    faqPayA3: 'Sim, oferecemos descontos progressivos para compras de múltiplos cursos e condições especiais para empresas. Entre em contato com nossa equipe comercial para uma proposta personalizada.',
    faqPayQ4: 'O preço inclui todos os materiais?',
    faqPayA4: 'Sim, o valor do curso inclui acesso completo às aulas, materiais complementares em PDF, exercícios práticos e certificado de conclusão.',
    faqCompQ1: 'Como funciona o treinamento corporativo?',
    faqCompA1: 'Oferecemos soluções personalizadas para empresas, incluindo plataforma white-label, gestão de equipes, relatórios de progresso e trilhas de aprendizado customizadas. Entre em contato para uma proposta sob medida.',
    faqCompQ2: 'Posso emitir nota fiscal no CNPJ da empresa?',
    faqCompA2: 'Sim, todas as compras podem ser faturadas no CNPJ da empresa. Basta informar os dados da empresa no momento da compra ou entrar em contato com nosso atendimento.',
    faqCompQ3: 'Existe um número mínimo de colaboradores?',
    faqCompA3: 'Não há número mínimo obrigatório, mas oferecemos condições especiais a partir de 5 matrículas. Para grupos maiores, desenvolvemos propostas personalizadas com benefícios exclusivos.',
    faqCompQ4: 'Posso acompanhar o progresso da minha equipe?',
    faqCompA4: 'Sim, no plano corporativo você tem acesso a um painel administrativo com relatórios de progresso, taxas de conclusão e desempenho de cada colaborador.',

    // About
    aboutHeroTitle: 'Sobre a DQS Academy Brasil',
    aboutHeroSubtitle: 'A excelência da DQS em certificação, agora aplicada ao desenvolvimento de competências para profissionais e organizações em todo o Brasil.',
    whoWeAre: 'Quem Somos',
    whoWeAreP1: 'A DQS Academy Brasil é o braço educacional da DQS, uma das maiores e mais respeitadas certificadoras do mundo. Com mais de 40 anos de experiência global em auditoria e certificação de sistemas de gestão, trazemos esse conhecimento para formar profissionais de excelência.',
    whoWeAreP2: 'Nossos cursos são desenvolvidos por auditores e especialistas que vivenciam diariamente a aplicação prática das normas nas mais diversas organizações e setores da economia.',
    whoWeAreP3: 'Acreditamos que o conhecimento é o caminho para a transformação. Por isso, nos dedicamos a oferecer treinamentos que realmente fazem diferença na carreira dos profissionais e nos resultados das organizações.',
    yearsGlobalExperience: 'Anos de Experiência Global',
    partOfGroup: 'Parte do Grupo DQS',
    partOfGroupSubtitle: 'A DQS é uma das organizações de certificação mais experientes do mundo, com presença em mais de 60 países e uma rede de milhares de auditores qualificados atendendo clientes de todos os portes e setores.',
    countries: 'Países',
    clients: 'Clientes',
    auditors: 'Auditores',
    standards: 'Normas',
    ourValues: 'Nossos Valores',
    ourValuesSubtitle: 'Os princípios que guiam nossa atuação e nosso compromisso com você.',
    valueExcellence: 'Excelência',
    valueExcellenceDesc: 'Compromisso com os mais altos padrões de qualidade em tudo o que fazemos.',
    valuePartnership: 'Parceria',
    valuePartnershipDesc: 'Construímos relacionamentos de longo prazo baseados em confiança e resultados.',
    valueKnowledge: 'Conhecimento',
    valueKnowledgeDesc: 'Compartilhamos expertise para desenvolver profissionais e organizações.',
    valueGlobalization: 'Globalização',
    valueGlobalizationDesc: 'Presença internacional com atuação local adaptada a cada mercado.',
    ourTrajectory: 'Nossa Trajetória',
    milestone1985: 'Fundação da DQS na Alemanha',
    milestone1990: 'Expansão para mercados internacionais',
    milestone1994: 'Início das operações no Brasil',
    milestone2010: 'Expansão de treinamentos e auditorias no Brasil',
    milestone2025: 'Criação da DQS Academy Brasil',
    milestoneToday: 'Líder em capacitação em sistemas de gestão',
    milestoneLabel: 'Hoje',
    educationalDifferential: 'Diferencial Educacional',
    educationalDifferentialP1: 'Nosso diferencial está na combinação única entre conhecimento técnico profundo e experiência prática de auditoria. Todos os nossos instrutores são profissionais ativos que conhecem os desafios reais das organizações.',
    educationalDifferentialP2: 'Os conteúdos são constantemente atualizados para refletir as últimas mudanças normativas e as melhores práticas de mercado, garantindo que você sempre aprenda o que há de mais relevante.',
    whyDQSAcademy: 'Por que a DQS Academy?',
    diffContent: 'Conteúdo desenvolvido por auditores experientes',
    diffMethodology: 'Metodologia focada na prática',
    diffCertificates: 'Certificados com reconhecimento internacional',
    diffSupport: 'Suporte técnico especializado',
    diffUpdated: 'Atualização contínua dos materiais',
    aboutCtaTitle: 'Comece sua jornada de desenvolvimento',
    aboutCtaSubtitle: 'Explore nossos cursos e descubra como a DQS Academy pode impulsionar sua carreira e sua organização.',

    // Para Empresas
    companiesHeroBadge: 'Soluções para Empresas',
    companiesHeroTitle: 'Capacitação Corporativa com Excelência DQS',
    companiesHeroSubtitle: 'Desenvolva competências estratégicas na sua equipe com quem entende de sistemas de gestão como ninguém.',
    requestContact: 'Solicitar Contato',
    useCasesTitle: 'Casos de Uso',
    useCasesSubtitle: 'Descubra como a DQS Academy pode apoiar os objetivos de capacitação da sua organização.',
    useCaseCertTitle: 'Certificação de Equipes',
    useCaseCertDesc: 'Prepare sua equipe para auditorias de certificação com treinamentos oficiais DQS.',
    useCaseDevTitle: 'Desenvolvimento Contínuo',
    useCaseDevDesc: 'Mantenha sua equipe atualizada com as últimas versões das normas ISO.',
    useCaseComplianceTitle: 'Compliance e Conformidade',
    useCaseComplianceDesc: 'Garanta que todos os colaboradores conheçam os requisitos normativos aplicáveis.',
    useCaseOnboardingTitle: 'Onboarding de Novos Colaboradores',
    useCaseOnboardingDesc: 'Integre novos membros com treinamentos padronizados sobre sistemas de gestão.',
    differentialsTitle: 'Diferenciais DQS Academy',
    differentialsSubtitle: 'Por que líderes de mercado escolhem a DQS para capacitar suas equipes.',
    diffGlobalExpertise: 'Expertise Global',
    diffGlobalExpertiseDesc: 'Mais de 40 anos de experiência em certificação e treinamento em sistemas de gestão.',
    diffUpdatedContent: 'Conteúdo Atualizado',
    diffUpdatedContentDesc: 'Cursos desenvolvidos e revisados por auditores ativos da DQS.',
    diffPracticalMethod: 'Metodologia Prática',
    diffPracticalMethodDesc: 'Foco em aplicação real com estudos de caso e exercícios práticos.',
    diffB2B: 'B2B & E-Learning',
    diffB2BDesc: 'Plataforma 100% online desenvolvida para empresas que buscam capacitar suas equipes em normas e sistemas de gestão.',
    diffDedicatedSupport: 'Suporte Dedicado',
    diffDedicatedSupportDesc: 'Equipe de atendimento corporativo para gerenciar sua conta.',
    diffRecognizedCert: 'Certificação Reconhecida',
    diffRecognizedCertDesc: 'Certificados com o selo DQS, aceitos internacionalmente.',
    companiesCtaTitle: 'Pronto para elevar o nível da sua equipe?',
    companiesCtaSubtitle: 'Nossa equipe está pronta para entender suas necessidades e propor a melhor solução de capacitação para sua empresa.',
    talkToSpecialist: 'Falar com Especialista',
    viewCompleteSolutions: 'Ver Soluções Completas',

    // Soluções Corporativas
    corpHeroTitle: 'Soluções Corporativas em Capacitação',
    corpHeroSubtitle: 'Transforme o conhecimento da sua equipe em resultados. A DQS Academy oferece programas de treinamento empresarial com a credibilidade de mais de 40 anos de experiência global.',
    talkToConsultantBtn: 'Fale com um Consultor',
    knowCourses: 'Conhecer Cursos',
    corpBenefitsTitle: 'Por que escolher a DQS Academy?',
    corpBenefitsSubtitle: 'Uma plataforma completa para gestão de treinamentos corporativos com a qualidade e confiabilidade que sua empresa precisa.',
    corpBenefitTeamTitle: 'Gestão de Equipes',
    corpBenefitTeamDesc: 'Administre o acesso e progresso de todos os colaboradores em uma única plataforma centralizada.',
    corpBenefitReportsTitle: 'Relatórios e Dashboards',
    corpBenefitReportsDesc: 'Acompanhe KPIs de treinamento, taxas de conclusão e desempenho em tempo real.',
    corpBenefitTrailsTitle: 'Trilhas Personalizadas',
    corpBenefitTrailsDesc: 'Crie jornadas de aprendizado customizadas para cada função ou departamento da sua empresa.',
    corpBenefitCertsTitle: 'Certificados Corporativos',
    corpBenefitCertsDesc: 'Emita certificados validados pela DQS com a identidade visual da sua organização.',
    corpHowItWorks: 'Como Funciona',
    corpHowItWorksSubtitle: 'Um processo simples e escalável para desenvolver competências com treinamentos certificados.',
    corpStep1Title: 'Diagnóstico',
    corpStep1Desc: 'Entendemos as necessidades de capacitação da sua organização.',
    corpStep2Title: 'Proposta Personalizada',
    corpStep2Desc: 'Desenvolvemos um plano de treinamento sob medida para sua equipe.',
    corpStep3Title: 'Liberação de Acessos',
    corpStep3Desc: 'Cadastre os colaboradores e inicie o acompanhamento imediato da capacitação na plataforma.',
    corpStep4Title: 'Acompanhamento',
    corpStep4Desc: 'Suporte contínuo e relatórios de evolução para garantir resultados.',
    corpMetricCompanies: 'Empresas Atendidas',
    corpMetricProfessionals: 'Profissionais Capacitados',
    corpMetricSatisfaction: 'Taxa de Satisfação',
    corpMetricExperience: 'Anos de Experiência DQS',
    corpFeaturesTitle: 'Tudo que sua empresa precisa em um só lugar',
    corpFeature1: 'Acesso corporativo à DQS Academy para capacitação certificável',
    corpFeature1Desc: 'Centralize o desenvolvimento das competências da sua equipe em uma única plataforma',
    corpFeature2: 'Cursos desenvolvidos por especialistas DQS',
    corpFeature3: 'Suporte técnico e pedagógico dedicado',
    corpFeature4: 'Acesso mobile para aprendizado em qualquer lugar',
    corpFeature5: 'Conteúdo atualizado conforme normas vigentes',
    corpFeature6: 'Certificados com validade internacional',
    corpReadyToStart: 'Pronto para começar?',
    corpReadySubtitle: 'Entre em contato com nossa equipe comercial e receba uma proposta personalizada para as necessidades da sua organização.',
    requestProposal: 'Solicitar Proposta',
    corpServiceBrazil: 'Atendimento em todo o Brasil',

    // Verificar Certificado
    certVerificationTitle: 'DQS Academy Brasil',
    certVerificationSubtitle: 'Verificação de Certificado',
    verifyingCert: 'Verificando certificado…',
    certValid: 'Certificado Válido',
    certValidSubtitle: 'Este certificado foi emitido pela DQS Academy Brasil e é autêntico.',
    certNotFound: 'Certificado não encontrado',
    certNotFoundSubtitle: 'O código informado não corresponde a nenhum certificado emitido pela DQS Academy Brasil. Verifique se o código está correto e tente novamente.',
    certStudentLabel: 'Aluno(a)',
    certCourseLabel: 'Curso',
    certIssueDateLabel: 'Data de Emissão',
    certCodeLabel: 'Código de Verificação',
    backToSite: 'Voltar ao site',

    // Not Found
    notFoundTitle: '404',
    notFoundSubtitle: 'Ops! Página não encontrada',
    notFoundDescription: 'O link pode estar quebrado ou a página foi removida. Tente uma das opções abaixo:',
    returnToHome: 'Voltar para o Início',
    exploreCatalog: 'Explorar Cursos',

    // Cart Dropdown
    yourCart: 'Seu Carrinho',
    continueShopping: 'Continuar comprando',
    courseWordSingular: 'curso',
    courseWordPlural: 'cursos',

    // Search Dialog
    searchCoursesLabel: 'Buscar cursos',
    noResultsFor: 'Nenhum curso encontrado para',
    resultsCount: 'resultado',
    resultWord: 'resultado',
    resultsWord: 'resultados',

    // Reviews
    reviewsTitle: 'Avaliações',
    reviewWord: 'avaliação',
    reviewsWordPlural: 'avaliações',
    noReviewsYet: 'Nenhuma avaliação ainda.',
    rateCourse: 'Avaliar curso',
    rateModalTitle: 'Avaliar curso',
    clickToRate: 'Clique para avaliar',
    starsOf: 'de 5 estrelas',
    leaveComment: 'Deixe um comentário (opcional)',
    submitReview: 'Enviar avaliação',
    submittingReview: 'Enviando...',
    reviewSuccess: 'Avaliação enviada com sucesso!',
    reviewError: 'Erro ao enviar avaliação.',
    selectRating: 'Selecione uma nota de 1 a 5 estrelas.',
    studentDefault: 'Aluno',

    // Language selector
    portuguese: 'Português',
    english: 'English',
    spanish: 'Español',

    // Contact Page
    contactPageTitle: 'Entre em Contato',
    contactPageSubtitle: 'Preencha o formulário abaixo e nossa equipe entrará em contato em breve.',
    contactName: 'Nome',
    contactCompany: 'Empresa',
    contactRole: 'Cargo',
    contactEmployees: 'Número de Colaboradores',
    contactCoursesInterest: 'Cursos de Interesse',
    contactOtherCourseLabel: 'Especifique o curso',
    contactOtherCoursePlaceholder: 'Qual curso você procura?',
    contactEmail: 'E-mail',
    contactPhone: 'Telefone',
    contactMessage: 'Mensagem',
    contactSubmit: 'Enviar Mensagem',
    contactSubmitting: 'Enviando...',
    contactSuccess: 'Mensagem enviada!',
    contactSuccessMessage: 'Obrigado pelo seu contato. Nossa equipe retornará em breve.',
    contactError: 'Erro ao enviar mensagem. Tente novamente.',

    // Lesson Player (chrome)
    lessonBack: 'Voltar',
    videoLesson: 'Videoaula',
    pdfMaterial: 'Material em PDF',
    quizType: 'Questionário',
    closeContent: 'Fechar Conteúdo',
    accessContent: 'Acessar Conteúdo',
    lessonCompletedToast: 'Aula concluída!',
    lessonCompleteError: 'Erro ao marcar aula como concluída.',
    certificateGeneratedToast: '🎉 Certificado gerado! Acesse a aba Certificados para baixar.',
    certificateRequestError: 'Erro ao solicitar certificado. Tente novamente mais tarde.',
    concludeCourseError: 'Erro ao concluir o curso. Tente novamente.',
    noCourseAccessTitle: 'Você não tem acesso a este curso',
    noCourseAccessDescription: 'Você precisa estar matriculado para acessar o conteúdo deste curso.',
    previousLesson: 'Anterior',
    nextLabel: 'Próxima',
    markAsCompleted: 'Marcar como concluída',
    lessonCompletedBadge: 'Concluída',
    completeQuizToAdvance: 'Complete o quiz com 70% ou mais para avançar',
    finishCourse: 'Concluir Curso',
    congratsCourseCompleted: 'Parabéns! Você concluiu o curso. O que achou?',

    // Lesson Blocks — shared
    howToNavigate: 'Como navegar pelo curso',
    attentionPoint: 'Ponto de Atenção',
    contentInProduction: 'Conteúdo em produção',
    watchVideo: 'Assistir vídeo',
    ofConnector: 'de',
    youGotCorrect: 'Você acertou',
    questionsWord: 'questões',
    classificationsWord: 'classificações',
    matchesWord: 'associações',
    nextLesson: 'Próxima Aula',
    scoreLabel: 'Pontuação:',
    retakeQuiz: 'Refazer Quiz',
    tryAgain: 'Tentar Novamente',

    // Quiz Block
    quizDefaultTitle: 'Quiz',
    activityCompleted: 'Atividade Concluída!',
    keepPracticing: 'Continue Praticando',
    excellentWork: 'Excelente trabalho!',
    goodWorkReviewScenarios: 'Bom trabalho! Revise os cenários que errou.',
    goodWorkReviewItems: 'Bom trabalho! Revise os itens que errou.',
    reviewConceptsTryAgain: 'Revise os conceitos e tente novamente para fixar melhor o conteúdo.',
    passedCongrats: '🎉 Parabéns! Você foi aprovado!',
    almostThere: 'Quase lá!',
    youNeed: 'Você precisa de',
    toPass: 'para aprovar.',
    youScored: 'Você fez',
    generatingCertificate: 'Gerando certificado...',
    certificateBeingGenerated: 'Seu certificado está sendo gerado e será enviado por e-mail em instantes.',
    confirmAnswer: 'Confirmar Resposta',
    nextQuestion: 'Próxima Questão',
    viewResult: 'Ver Resultado',
    questionLabel: 'Questão',
    minimumScore: 'Nota mínima:',
    correctLabel: 'Correto!',
    incorrectLabel: 'Incorreto',
    correctAnswerIs: 'A resposta correta é:',

    // Classification Quiz Block
    classifyContext: 'Classifique o Contexto',
    yourAnswer: 'Sua resposta:',
    correctColon: 'Correto:',
    scenarioLabel: 'Cenário:',
    eventLabel: 'EVENTO',
    confirmLabel: 'Confirmar',
    nextItemLabel: 'Próximo',

    // Auditor Scenario Block
    hideAnalysis: 'Ocultar Análise',
    revealAnalysis: 'Revelar Análise',
    scenarioAnalysisLabel: 'ANÁLISE DO CENÁRIO',
    scenarioAnalysisTitle: 'Análise do Cenário',
    internalLabel: 'Interno',
    externalLabel: 'Externo',
    selectRatingStars: 'Selecione uma nota de 1 a 5 estrelas.',
    ratingSubmittedToast: 'Avaliação enviada! 🎉 Certificado disponível na aba Certificados.',
    submitRatingError: 'Erro ao enviar avaliação. Tente novamente.',
    courseCompletedSuccess: 'Parabéns! Você concluiu o curso com sucesso!',
    stepCompleted: 'Resposta correta! Etapa concluída.',
    answerCorrectStepDone: 'Sua resposta está correta. Esta etapa foi concluída com sucesso.',
    advanceNextLesson: 'Muito bem! Avance para a próxima aula usando o botão abaixo.',
    reviewQuiz: 'Rever questionário',
    whatDidYouThink: 'O que achou do curso?',
    ratingHelperText: 'Sua avaliação aparece abaixo da mensagem de conclusão e não fecha esta tela.',
    outOfFiveStars: 'de 5 estrelas',
    commentPlaceholder: 'Deixe um comentário (opcional)',
    submitRating: 'Enviar avaliação',
    thanksForRating: 'Obrigado pela sua avaliação!',
    correctOption: 'Correta',
    incorrectAnswerTryAgain: 'Resposta incorreta. Tente novamente.',

    // Checklist Block
    auditChecklistTitle: 'Checklist de Auditoria',
    verifiedCount: 'verificados',
    checklistHelper: 'Clique no checkbox para verificar, clique na pergunta para expandir detalhes',
    verifyLabel: 'Verificar',
    uncheckLabel: 'Desmarcar',
    evidenceToRequest: 'Evidências a solicitar',
    checklistCompleteTitle: 'Checklist completo!',
    checklistCompleteMessage: 'Você revisou todos os pontos de verificação. Use este guia durante suas auditorias reais.',

    // Matching Block
    termsLabel: 'Termos',
    definitionsLabel: 'Definições',
    dragDefinitionHere: 'Arraste uma definição aqui',
    allDefinitionsMatched: 'Todas as definições foram associadas',
    perfectAllCorrect: '🎉 Perfeito! Todas as associações estão corretas!',
    verifyAnswers: 'Verificar Respostas',

    // Scenario Block(s)
    situationLabel: 'Situação',
    questionWordLabel: 'Pergunta',
    viewAnswer: 'Ver Resposta',
    hideAnswer: 'Ocultar Resposta',
    answerLabel: 'Resposta',
    expertTip: 'Dica do Especialista',
    scenarioWord: 'Cenário',

    // PDF / Flip / Media Blocks
    downloadPdfMaterial: 'Baixar Material PDF',
    clickToFlip: 'Clique para virar',
    definitionLabel: 'Definição',
    materialInProduction: 'Material em produção',
    openMaterial: 'Abrir material',

    // Sidebar
    collapseLabel: 'Recolher',
    expandLabel: 'Expandir',
    moduleLockedTooltip: '🔒 Módulo bloqueado',
    lockedLabel: 'Bloqueado',
    completedFemPlural: 'concluídas',
    unlockModuleTooltip: 'Complete o quiz do módulo anterior com 70% ou mais para desbloquear',

    // Block Error Boundary
    blockRenderErrorTitle: 'Não foi possível exibir este conteúdo.',
    blockRenderErrorMessage: 'Avise o suporte (kaio.souza@dqs.de) — o restante da aula continua disponível abaixo.',
  },

  'en': {
    // Navigation
    home: 'Home',
    courses: 'Courses',
    categories: 'Categories',
    forCompanies: 'For Companies',
    about: 'About',
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    myCourses: 'My Courses',
    myProfile: 'My Profile',
    helpAndSupport: 'Help & Support',
    certificates: 'Certificates',
    search: 'Search',
    searchPlaceholder: 'Search courses...',
    faq: 'FAQ',

    // Categories
    quality: 'Quality',
    environment: 'Environment',
    workSafety: 'Occupational Safety',
    infoSecurity: 'Information Security',
    automotive: 'Automotive',
    foodSafety: 'Food Safety',
    technology: 'Technology',
    businessContinuity: 'Business Continuity',
    compliance: 'Compliance',

    // Common
    viewDetails: 'View details',
    enrollNow: 'Enroll now',
    startNow: 'Start now',
    learnMore: 'Learn more',
    cart: 'Cart',
    emptyCart: 'Your cart is empty',
    goToCheckout: 'Go to checkout',
    searchCourses: 'Search courses',
    remove: 'Remove',
    total: 'Total',
    backToHome: 'Back to home',
    backToLogin: 'Back to login',
    backToCourses: 'Back to Courses',
    backToCourse: 'Back to course',
    free: 'Free',
    loading: 'Loading...',
    saving: 'Saving...',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    or: 'or',
    and: 'and',
    yes: 'Yes',
    no: 'No',

    // Category Pages
    categoryTitle: 'Category',
    categoryDescription: 'Explore our courses in',
    noCourses: 'No courses found in this category.',
    categoryNotFound: 'Category not found',
    viewAllCategories: 'View all categories',
    coursesIn: 'Courses in',
    comingSoonCategory: 'Courses in this category coming soon.',
    exploreOtherCourses: 'Explore other courses',
    viewCourses: 'View courses',

    // Categories Index
    categoriesHeroTitle: 'Categories',
    categoriesHeroSubtitle: 'Explore our courses organized by area of expertise. Find specialized training for every need in your organization.',
    categoriesNotFoundTitle: "Didn't find what you're looking for?",
    categoriesNotFoundSubtitle: 'Explore our full catalog with advanced filters or contact us for customized solutions.',
    exploreAllCourses: 'Explore All Courses',

    // Hero Section
    heroBadge: 'World leader in management system certification',
    heroTitle1: 'Develop your skills in',
    heroTitle2: 'Management Systems',
    heroSubtitle: 'Online courses developed by DQS experts, with practical methodology and market-recognized certificates.',
    exploreCourses: 'Explore Courses',
    corporateSolutions: 'Corporate Solutions',
    availableCourses: 'Available Courses',
    coursesCompleted: 'Courses Completed',
    trainedProfessionals: 'Trained Professionals',
    yearsExperience: 'Years of Experience',

    // Featured Courses
    featuredCourses: 'Featured Courses',
    featuredCoursesSubtitle: 'The most sought-after courses by professionals and companies',
    viewAllCourses: 'View all courses',
    bestSeller: 'Best Seller',
    newCourse: 'New',
    hours: 'hours',
    students: 'students',

    // Why DQS
    whyDQS: 'Why choose DQS Academy?',
    whyDQSSubtitle: "The credibility of one of the world's largest certifiers, now in a corporate education platform.",
    globalExpertise: 'Global Expertise',
    globalExpertiseDesc: 'Over 35 years of experience in management system certification worldwide.',
    specializedInstructors: 'Specialized Instructors',
    specializedInstructorsDesc: 'Auditors and consultants with hands-on experience in implementing standards.',
    recognizedCertificates: 'Recognized Certificates',
    recognizedCertificatesDesc: 'Nationally valid certificates issued by DQS Academy Brasil.',
    practicalMethodology: 'Practical Methodology',
    practicalMethodologyDesc: 'Applicable content with real-world case studies and exercises.',
    continuousSupport: 'Continuous Support',
    continuousSupportDesc: 'Access to supplementary materials and specialized technical support.',
    professionalCommunity: 'Professional Community',
    professionalCommunityDesc: 'Networking with thousands of professionals in the management systems field.',
    alignedWithStandards: 'Courses aligned with major international standards',
    certified: 'Certified',

    // Categories Section
    exploreByCategory: 'Explore by Category',
    exploreByCategorySubtitle: 'Find specialized courses in various areas of management systems',
    coursesCount: 'courses',
    categorySectionQualityDesc: 'ISO 9001, Total Quality Management, Quality Tools',
    categorySectionEnvironmentDesc: 'ISO 14001, Environmental Management, Corporate Sustainability',
    categorySectionSafetyDesc: 'ISO 45001, OHS, Accident Prevention',
    categorySectionInfoSecDesc: 'ISO 27001, GDPR, Cybersecurity',
    categorySectionAutomotiveDesc: 'IATF 16949, VDA, Core Tools',
    categorySectionFoodDesc: 'ISO 22000, FSSC, HACCP',

    // Corporate Section
    corporateSolutionsBadge: 'Corporate Solutions',
    corporateTitle: 'Empower your team with DQS expertise',
    corporateSubtitle: 'Offer high-quality training for your organization. Manage enrollments, track progress and develop competencies in management systems.',
    teamManagement: 'Team Management',
    teamManagementDesc: 'Assign courses and track the progress of all employees.',
    detailedReports: 'Detailed Reports',
    detailedReportsDesc: 'Dashboards with engagement and completion metrics by team.',
    corporateCertificates: 'Corporate Certificates',
    corporateCertificatesDesc: "Customized certificates with your company's branding.",
    learnSolutions: 'Discover Solutions',
    talkToConsultant: 'Talk to a Consultant',
    companiesServed: 'Companies Served',
    employeesTrained: 'Employees Trained',
    satisfaction: 'Satisfaction',
    platformAccess: 'Platform Access',
    exclusivePortal: 'Exclusive Portal',
    realTimeReports: 'Real-Time Reports',

    // CTA Section
    startYourJourney: 'Start your journey today',
    readyToDevelop: 'Ready to develop your skills?',
    ctaSubtitle: 'Join thousands of professionals who have advanced their careers with DQS Academy Brasil courses.',
    createFreeAccount: 'Create Free Account',
    freeSignup: 'Free signup • Immediate access • No commitment',

    // Footer
    footerTagline: 'Excellence in Training',
    footerDescription: 'Developing competencies in management systems with the credibility and expertise of DQS, a world leader in certification.',
    coursesTitle: 'Courses',
    categoriesTitle: 'Categories',
    institutionalTitle: 'Institutional',
    legalTitle: 'Legal',
    aboutDQS: 'About DQS Academy',
    contact: 'Contact',
    termsOfUse: 'Terms of Use',
    privacyPolicy: 'Privacy Policy',
    cookiePolicy: 'Cookie Policy',
    allRightsReserved: 'All rights reserved.',
    companyGroup: 'A company of the group',

    // Auth — Login
    welcomeBack: 'Welcome back',
    loginSubtitle: 'Sign in to your account to continue',
    email: 'Email',
    password: 'Password',
    forgotPassword: 'Forgot password?',
    noAccount: "Don't have an account?",
    createAccount: 'Create account',
    loginTab: 'Sign In',
    createAccountTab: 'Create Account',
    loggingIn: 'Signing in...',
    loginButton: 'Sign In',
    orContinueWith: 'or continue with',
    invalidCredentials: 'Invalid email or password',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordTooShort: 'Password must contain at least 8 characters, including uppercase, lowercase letters and numbers.',
    accountCreatedSuccess: 'Account created! Check your email to confirm your registration.',
    accountCreatedError: 'Error creating account. Please try again.',

    // Auth — Register
    createAccountTitle: 'Create your account',
    registerSubtitle: 'Sign up to access our courses',
    fullName: 'Full name',
    yourName: 'Your name',
    confirmPassword: 'Confirm password',
    repeatPassword: 'Repeat password',
    alreadyHaveAccount: 'Already have an account?',
    creatingAccount: 'Creating account...',
    termsAgreement: 'I have read and accept the',
    marketingConsent: 'I want to receive news and offers by email',
    minCharsPassword: 'Minimum 6 characters',
    cpf: 'CPF',
    invalidCpf: 'Invalid CPF',

    // Auth — Register (Cadastrar page)
    joinDQSAcademy: 'Join DQS Academy',
    joinSubtitle: 'Create your account and access the best courses in management systems developed by DQS experts.',
    benefitHighQuality: 'Access to high-quality courses',
    benefitInternationalCerts: 'Internationally recognized certificates',
    benefitExpertContent: 'Content developed by DQS experts',
    benefitSupport: 'Technical and educational support',
    accountCreatedTitle: 'Account created successfully!',
    confirmEmailSent: 'We sent a confirmation email to',
    checkSpam: "Didn't receive it? Check your spam folder.",
    resendEmail: 'Resend confirmation email',
    resendingEmail: 'Resending...',
    fillAllFields: 'Please fill in all fields.',
    fillYourData: 'Fill in your details to get started',
    emailResent: 'Email resent!',
    emailResendError: 'Error resending email',
    acceptTermsRequired: 'You must accept the terms of use to continue.',

    // Auth — Recovery
    recoverPassword: 'Recover password',
    recoverPasswordSubtitle: 'Enter your email and we will send you a link to reset your password.',
    sendRecoveryLink: 'Send recovery link',
    sendingEmail: 'Sending...',
    emailSent: 'Email sent!',
    recoverySentMessage: 'We sent a recovery link to',
    sendError: 'Error sending email',

    // Auth — Reset Password
    resetPassword: 'Reset password',
    resetPasswordSubtitle: 'Choose a new password for your account.',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    resetButton: 'Reset password',
    resettingPassword: 'Saving...',
    passwordResetSuccess: 'Password reset!',
    passwordResetSuccessMessage: 'Your password has been changed successfully. Redirecting to login...',
    passwordResetError: 'Error resetting password',

    // Student Area
    studentArea: 'Student Area',
    myCoursesTab: 'My Courses',
    profileTab: 'My Profile',
    certificatesTab: 'Certificates',
    libraryTab: 'Library',
    libraryInfoTitle: 'Support materials',
    libraryInfoSubtitle: 'Handbooks, ebooks and slides from internal training, available whenever you need them.',
    libraryInternalBadge: 'DQS internal use',
    libraryEmpty: 'No material published yet',
    libraryEmptySubtitle: 'As soon as the training materials are published, they will show up here.',
    libraryOpenFile: 'Download',
    libraryOpenLink: 'Open',
    aiDisclosureLesson: 'Presenter image and voice generated by AI. Technical content created and reviewed by DQS specialists in the standard covered by this course.',
    aiDisclosureDocument: 'Material produced with AI assistance and reviewed by DQS specialists in the standard covered by this course.',
    aiDisclosurePreview: 'Videos with AI-generated image and voice. Materials produced with AI assistance. All technical content is created and reviewed by DQS specialists in the standard covered by the course.',
    aiDisclosurePreviewNoVideo: 'Materials produced with AI assistance. All technical content is created and reviewed by DQS specialists in the standard covered by the course.',
    aiDisclosureAriaLabel: 'Notice about the use of artificial intelligence',
    hello: 'Hello',
    welcomeToLearning: 'Welcome to your learning area',

    // My Courses
    inProgress: 'In progress',
    completed: 'Completed',
    notStarted: 'Not started',
    continueWatching: 'Continue',
    startCourse: 'Start',
    reviewCourse: 'Review Course',
    enrolledCourses: 'Enrolled courses',
    yourCourses: 'Your courses',
    noCoursesYet: 'You are not enrolled in any courses yet.',
    noCoursesSubtitle: 'Explore our catalog and start learning.',
    lessons: 'lessons',
    lastAccessed: 'Last accessed:',
    today: 'Today',
    yesterday: 'Yesterday',
    daysAgo: 'days ago',
    percentCompleted: 'completed',

    // My Certificates
    downloadCertificate: 'Download PDF',
    viewCertificate: 'View',
    noCertificatesYet: 'No certificates yet',
    noCertificatesSubtitle: 'Complete a course to receive your certificate.',
    certificatesInfoTitle: 'DQS Academy Certificates',
    certificatesInfoSubtitle: 'Certificates are automatically issued after completing 100% of the course. Each certificate has a unique validation code that can be verified on our portal.',
    issuedOn: 'Issued on:',
    verificationCode: 'Code:',
    downloadPdf: 'Download PDF',
    certificateValidation: 'Certificate Validation',
    certificateValidationDesc: 'All certificates issued by DQS Academy Brasil can be validated through the unique code on the document.',

    // My Profile
    personalInfo: 'Personal Information',
    personalInfoSubtitle: 'Your basic registration data',
    professionalInfo: 'Professional Information',
    professionalInfoSubtitle: 'Your company and position (optional)',
    preferences: 'Preferences',
    security: 'Security',
    securitySubtitle: 'Manage your password and account security',
    company: 'Company',
    companyPlaceholder: 'Company name',
    position: 'Position',
    positionPlaceholder: 'Your current position',
    fieldOfWork: 'Field of work',
    fieldOfWorkPlaceholder: 'E.g. Quality Management',
    areaOfInterest: 'Main area of interest',
    selectArea: 'Select an area',
    changePassword: 'Change password',
    changePasswordSubtitle: 'Change your access password',
    currentPassword: 'Current password',
    saveChanges: 'Save changes',
    savingChanges: 'Saving...',
    profileSaved: 'Profile updated successfully!',
    profileSaveError: 'Error saving',
    passwordChanged: 'Password changed successfully!',
    passwordChangeError: 'Error changing password',
    currentPasswordIncorrect: 'Current password is incorrect',
    accountStatus: 'Account Status',
    accountStatusSubtitle: 'Information about your account',
    registrationDate: 'Registration date',
    accessType: 'Access type',
    status: 'Status',
    active: 'Active',
    roleStudent: 'Student',
    roleAdmin: 'Administrator',
    roleModerator: 'Moderator',
    contactSupport: 'Contact support if needed',
    passwordDialogTitle: 'Change password',
    passwordDialogSubtitle: 'Enter your current password and the new password you want.',
    changingPassword: 'Changing...',

    interestQuality: 'Quality (ISO 9001)',
    interestEnvironment: 'Environment (ISO 14001)',
    interestSafety: 'Health & Safety (ISO 45001)',
    interestIntegrated: 'Integrated Management System',
    interestInfoSec: 'Information Security (ISO 27001)',
    interestAudit: 'Auditing',
    interestOther: 'Other',

    // Cursos (Catalog)
    catalogTitle: 'Course Catalog',
    catalogSubtitle: 'Explore our selection of management system courses, developed by DQS experts with practical methodology and recognized certificates.',
    searchCoursesPlaceholder: 'Search courses...',
    sortRelevance: 'Relevance',
    sortPopular: 'Most Popular',
    sortTopRated: 'Top Rated',
    sortLowestPrice: 'Lowest Price',
    sortHighestPrice: 'Highest Price',
    coursesFound: 'course',
    courseWord: 'course',
    coursesWord: 'courses',
    noCoursesFound: 'No courses found',
    noCoursesFoundSubtitle: 'Try adjusting filters or searching again.',
    loadError: 'Error loading courses. Please try again later.',
    continueCourse: 'Continue course',
    viewCertificate: 'View Certificate',
    enrollErrorTitle: 'Error starting course',
    enrollErrorDescription: 'Please try again.',

    // Course Filters
    filters: 'Filters',
    clearFilters: 'Clear',
    filterCategory: 'Category',
    filterStandard: 'Standard',

    // Course Detail
    backToCatalog: 'Back to Courses',
    reviews: 'reviews',
    reviewsCount: 'reviews',
    evaluations: 'reviews',
    ofContent: 'of content',
    lessonsCount: 'lessons',
    moduleWord: 'module',
    modulesWord: 'modules',
    coursePreview: 'Course preview',
    progress: 'Progress',
    accessCourse: 'Access course',
    buyNow: 'Buy Now',
    immediateAccess: 'Immediate access',
    certificateIncluded: 'Certificate included',
    courseIncludes: 'This course includes:',
    videoLessons: 'video lessons',
    completionCertificate: 'Completion certificate',
    oneYearAccess: '1 year of access',
    whatYouWillLearn: 'What you will learn',
    courseContent: 'Course content',
    totalDuration: 'total duration',
    forWhomTitle: 'Who is this course for',
    prerequisites: 'Prerequisites',
    includedMaterials: 'Included materials',
    readyToStart: 'Ready to start your journey?',
    readyToStartSubtitle: 'Invest in your professional development and stand out in the market.',
    viewOtherCourses: 'View other courses',
    paymentConfirmed: 'Payment confirmed! Welcome to the course 🎉',
    paymentError: 'Error starting payment. Please try again.',
    noPaymentUrl: 'No payment URL returned. Please check the Stripe configuration.',
    serverError: 'Error connecting to the payment server.',
    installmentsOf: 'or up to',
    videoType: 'Video',

    // Checkout
    payment: 'Payment',
    cardTab: 'Card',
    pixTab: 'PIX',
    boletoTab: 'Boleto',
    cardNumber: 'Card number',
    cardName: 'Name on card',
    cardNamePlaceholder: 'As printed on your card',
    expiry: 'Expiry',
    cvv: 'CVV',
    installments: 'Installments',
    installmentSingle: 'one-time',
    installmentMultiple: 'x of R$',
    finishPayment: 'Finish payment',
    processingPayment: 'Processing payment...',
    originalPrice: 'Original price',
    securePurchase: 'Secure purchase',
    sslCertificate: 'SSL Certificate',
    sevenDayGuarantee: '7-day guarantee',
    scanQrCode: 'Scan the QR Code with your banking app',
    codeExpiresIn: 'This code expires in',
    copyPixKey: 'Copy PIX key',
    boletoExpiresIn: 'The boleto expires in',
    generateBoleto: 'Generate boleto',
    boletoConfirmation: 'After payment, confirmation may take up to 3 business days.',
    comingSoon: 'Coming soon! Stripe integration in progress.',
    comingSoonBadge: 'Coming soon',

    // Checkout Success
    paymentConfirmedTitle: 'Payment confirmed!',
    paymentProcessingSubtitle: 'Your enrollment is being processed. You will have access to the course shortly.',
    goToMyCourses: 'Go to My Courses',
    validationFailed: 'Please review the form',
    processing: 'Processing...',
    startCourseNow: 'Start the course now',
    whatHappensNext: 'What happens next?',
    nextStepAccessTitle: 'Access granted immediately',
    nextStepAccessDesc: 'Your enrollment is active. You can start studying right now.',
    nextStepEmailTitle: 'Confirmation email sent',
    nextStepEmailDesc: 'In a few minutes you receive the invoice and access link via email.',
    nextStepStudyTitle: 'Study at your own pace',
    nextStepStudyDesc: 'Lifetime access. Pause and resume anytime, on any device.',
    nextStepCertTitle: 'Automatic certificate',
    nextStepCertDesc: 'Once you complete 100% of the lessons, the certificate appears in your student area.',
    checkoutSuccessSupport: 'Any questions?',
    viewCourse: 'View course',

    // FAQ
    faqTitle: 'Frequently Asked Questions',
    faqSubtitle: 'Find answers to the most common questions about our courses, certificates, payments, and corporate solutions.',
    faqCategoryCoursesTitle: 'Courses',
    faqCategoryCertificatesTitle: 'Certificates',
    faqCategoryPaymentTitle: 'Payment',
    faqCategoryCompaniesTitle: 'Companies',
    faqNotFoundTitle: "Didn't find your answer?",
    faqNotFoundSubtitle: 'Our team is ready to help. Contact us and we will be happy to answer your questions.',
    faqContactButton: 'Contact Support',
    faqExploreButton: 'Explore Courses',
    faqCourseQ1: 'What types of courses does DQS Academy offer?',
    faqCourseA1: 'We offer courses in various areas of management systems, including Quality (ISO 9001), Environment (ISO 14001), Occupational Safety (ISO 45001), Information Security (ISO 27001), and Automotive (IATF 16949). Courses are available in e-learning, live, and hybrid formats.',
    faqCourseQ2: 'What is the average course duration?',
    faqCourseA2: 'Duration varies by course and depth level. Introductory courses typically range from 8 to 16 hours, while more comprehensive programs can reach 32 hours or more. The workload is always indicated on each course page.',
    faqCourseQ3: 'Are the courses updated?',
    faqCourseA3: 'Yes, all our courses are developed and reviewed by DQS auditors and experts, ensuring content is always aligned with the latest versions of standards and market best practices.',
    faqCourseQ4: 'Can I access the content after completion?',
    faqCourseA4: 'Yes, you maintain access to course content for 12 months after enrollment. This allows you to review the material whenever needed.',
    faqCertQ1: 'How does certificate issuance work?',
    faqCertA1: 'Upon completing 100% of lessons and achieving the minimum grade on assessments (when applicable), the certificate is automatically issued and available for download in your student area.',
    faqCertQ2: 'Does the certificate have international validity?',
    faqCertA2: 'Yes, DQS Academy certificates are internationally recognized, as they carry the seal of DQS, one of the most respected certifiers in the world with presence in over 60 countries.',
    faqCertQ3: 'Is the certificate valid for professional proof?',
    faqCertA3: 'Yes, our certificates are valid for proving professional qualifications and can be used in selection processes, audits, and to meet competency requirements.',
    faqPayQ1: 'What payment methods are accepted?',
    faqPayA1: 'We accept credit cards (up to 12 installments), bank slips, and PIX. For corporate purchases, we also offer direct billing.',
    faqPayQ2: 'Can I request a refund?',
    faqPayA2: 'Yes, we offer a 7-day guarantee. If you are not satisfied with the course, you can request a full refund within this period, as long as you have not completed more than 30% of the content.',
    faqPayQ3: 'Are there discounts for purchasing multiple courses?',
    faqPayA3: 'Yes, we offer progressive discounts for multiple course purchases and special conditions for companies. Contact our sales team for a customized proposal.',
    faqPayQ4: 'Does the price include all materials?',
    faqPayA4: 'Yes, the course price includes full access to lessons, supplementary PDF materials, practical exercises, and completion certificate.',
    faqCompQ1: 'How does corporate training work?',
    faqCompA1: 'We offer customized solutions for companies, including white-label platform, team management, progress reports, and custom learning paths. Contact us for a tailored proposal.',
    faqCompQ2: 'Can I issue an invoice to the company?',
    faqCompA2: "Yes, all purchases can be billed to the company. Simply provide the company details at the time of purchase or contact our support team.",
    faqCompQ3: 'Is there a minimum number of employees?',
    faqCompA3: 'There is no mandatory minimum, but we offer special conditions starting from 5 enrollments. For larger groups, we develop customized proposals with exclusive benefits.',
    faqCompQ4: "Can I track my team's progress?",
    faqCompA4: 'Yes, in the corporate plan you have access to an administrative dashboard with progress reports, completion rates, and performance data for each employee.',

    // About
    aboutHeroTitle: 'About DQS Academy Brasil',
    aboutHeroSubtitle: "DQS's excellence in certification, now applied to developing competencies for professionals and organizations across Brazil.",
    whoWeAre: 'Who We Are',
    whoWeAreP1: "DQS Academy Brasil is the educational arm of DQS, one of the world's largest and most respected certifiers. With over 40 years of global experience in management system auditing and certification, we bring this knowledge to train professionals of excellence.",
    whoWeAreP2: 'Our courses are developed by auditors and experts who experience the practical application of standards daily across diverse organizations and economic sectors.',
    whoWeAreP3: 'We believe knowledge is the path to transformation. That is why we are dedicated to offering training that truly makes a difference in professionals\' careers and organizational outcomes.',
    yearsGlobalExperience: 'Years of Global Experience',
    partOfGroup: 'Part of the DQS Group',
    partOfGroupSubtitle: "DQS is one of the world's most experienced certification organizations, with presence in over 60 countries and a network of thousands of qualified auditors serving clients of all sizes and sectors.",
    countries: 'Countries',
    clients: 'Clients',
    auditors: 'Auditors',
    standards: 'Standards',
    ourValues: 'Our Values',
    ourValuesSubtitle: 'The principles that guide our actions and our commitment to you.',
    valueExcellence: 'Excellence',
    valueExcellenceDesc: 'Commitment to the highest quality standards in everything we do.',
    valuePartnership: 'Partnership',
    valuePartnershipDesc: 'Building long-term relationships based on trust and results.',
    valueKnowledge: 'Knowledge',
    valueKnowledgeDesc: 'Sharing expertise to develop professionals and organizations.',
    valueGlobalization: 'Globalization',
    valueGlobalizationDesc: 'International presence with local operations adapted to each market.',
    ourTrajectory: 'Our Trajectory',
    milestone1985: 'DQS founded in Germany',
    milestone1990: 'Expansion to international markets',
    milestone1994: 'Operations begin in Brazil',
    milestone2010: 'Training and audit expansion in Brazil',
    milestone2025: 'Launch of DQS Academy Brasil',
    milestoneToday: 'Leader in management systems training',
    milestoneLabel: 'Today',
    educationalDifferential: 'Educational Differential',
    educationalDifferentialP1: 'Our differential lies in the unique combination of deep technical knowledge and practical auditing experience. All our instructors are active professionals who understand the real challenges of organizations.',
    educationalDifferentialP2: 'Content is constantly updated to reflect the latest regulatory changes and market best practices, ensuring you always learn what is most relevant.',
    whyDQSAcademy: 'Why DQS Academy?',
    diffContent: 'Content developed by experienced auditors',
    diffMethodology: 'Practice-focused methodology',
    diffCertificates: 'Internationally recognized certificates',
    diffSupport: 'Specialized technical support',
    diffUpdated: 'Continuously updated materials',
    aboutCtaTitle: 'Start your development journey',
    aboutCtaSubtitle: 'Explore our courses and discover how DQS Academy can boost your career and your organization.',

    // Para Empresas
    companiesHeroBadge: 'Solutions for Companies',
    companiesHeroTitle: 'Corporate Training with DQS Excellence',
    companiesHeroSubtitle: 'Develop strategic competencies in your team with the foremost experts in management systems.',
    requestContact: 'Request Contact',
    useCasesTitle: 'Use Cases',
    useCasesSubtitle: "Discover how DQS Academy can support your organization's training objectives.",
    useCaseCertTitle: 'Team Certification',
    useCaseCertDesc: 'Prepare your team for certification audits with official DQS training.',
    useCaseDevTitle: 'Continuous Development',
    useCaseDevDesc: 'Keep your team updated with the latest versions of ISO standards.',
    useCaseComplianceTitle: 'Compliance & Conformity',
    useCaseComplianceDesc: 'Ensure all employees know the applicable regulatory requirements.',
    useCaseOnboardingTitle: 'New Employee Onboarding',
    useCaseOnboardingDesc: 'Integrate new members with standardized training on management systems.',
    differentialsTitle: 'DQS Academy Differentials',
    differentialsSubtitle: 'Why market leaders choose DQS to train their teams.',
    diffGlobalExpertise: 'Global Expertise',
    diffGlobalExpertiseDesc: 'Over 40 years of experience in management system certification and training.',
    diffUpdatedContent: 'Updated Content',
    diffUpdatedContentDesc: 'Courses developed and reviewed by active DQS auditors.',
    diffPracticalMethod: 'Practical Methodology',
    diffPracticalMethodDesc: 'Focus on real-world application with case studies and hands-on exercises.',
    diffB2B: 'B2B & E-Learning',
    diffB2BDesc: '100% online platform designed for companies seeking to train their teams in standards and management systems.',
    diffDedicatedSupport: 'Dedicated Support',
    diffDedicatedSupportDesc: 'Corporate support team to manage your account.',
    diffRecognizedCert: 'Recognized Certification',
    diffRecognizedCertDesc: 'Certificates with the DQS seal, accepted internationally.',
    companiesCtaTitle: "Ready to take your team to the next level?",
    companiesCtaSubtitle: 'Our team is ready to understand your needs and propose the best training solution for your company.',
    talkToSpecialist: 'Talk to a Specialist',
    viewCompleteSolutions: 'View Complete Solutions',

    // Soluções Corporativas
    corpHeroTitle: 'Corporate Training Solutions',
    corpHeroSubtitle: "Transform your team's knowledge into results. DQS Academy offers enterprise training programs with the credibility of over 40 years of global experience.",
    talkToConsultantBtn: 'Talk to a Consultant',
    knowCourses: 'View Courses',
    corpBenefitsTitle: 'Why choose DQS Academy?',
    corpBenefitsSubtitle: 'A complete platform for corporate training management with the quality and reliability your company needs.',
    corpBenefitTeamTitle: 'Team Management',
    corpBenefitTeamDesc: 'Manage employee access and progress from a single centralized platform.',
    corpBenefitReportsTitle: 'Reports & Dashboards',
    corpBenefitReportsDesc: 'Track training KPIs, completion rates, and performance in real time.',
    corpBenefitTrailsTitle: 'Custom Learning Paths',
    corpBenefitTrailsDesc: 'Create customized learning journeys for each role or department in your company.',
    corpBenefitCertsTitle: 'Corporate Certificates',
    corpBenefitCertsDesc: "Issue DQS-validated certificates with your organization's visual identity.",
    corpHowItWorks: 'How It Works',
    corpHowItWorksSubtitle: 'A simple and scalable process for developing competencies with certified training.',
    corpStep1Title: 'Assessment',
    corpStep1Desc: "We understand your organization's training needs.",
    corpStep2Title: 'Custom Proposal',
    corpStep2Desc: 'We develop a tailored training plan for your team.',
    corpStep3Title: 'Access Setup',
    corpStep3Desc: 'Register employees and begin immediate tracking of training on the platform.',
    corpStep4Title: 'Follow-up',
    corpStep4Desc: 'Continuous support and progress reports to ensure results.',
    corpMetricCompanies: 'Companies Served',
    corpMetricProfessionals: 'Professionals Trained',
    corpMetricSatisfaction: 'Satisfaction Rate',
    corpMetricExperience: 'Years of DQS Experience',
    corpFeaturesTitle: 'Everything your company needs in one place',
    corpFeature1: 'Corporate access to DQS Academy for certifiable training',
    corpFeature1Desc: "Centralize your team's skill development on a single platform",
    corpFeature2: 'Courses developed by DQS experts',
    corpFeature3: 'Dedicated technical and educational support',
    corpFeature4: 'Mobile access for learning anywhere',
    corpFeature5: 'Content updated according to current standards',
    corpFeature6: 'Internationally valid certificates',
    corpReadyToStart: 'Ready to get started?',
    corpReadySubtitle: "Contact our sales team and receive a customized proposal for your organization's needs.",
    requestProposal: 'Request Proposal',
    corpServiceBrazil: 'Serving all of Brazil',

    // Verificar Certificado
    certVerificationTitle: 'DQS Academy Brasil',
    certVerificationSubtitle: 'Certificate Verification',
    verifyingCert: 'Verifying certificate…',
    certValid: 'Valid Certificate',
    certValidSubtitle: 'This certificate was issued by DQS Academy Brasil and is authentic.',
    certNotFound: 'Certificate not found',
    certNotFoundSubtitle: 'The code provided does not match any certificate issued by DQS Academy Brasil. Please verify the code and try again.',
    certStudentLabel: 'Student',
    certCourseLabel: 'Course',
    certIssueDateLabel: 'Issue Date',
    certCodeLabel: 'Verification Code',
    backToSite: 'Back to site',

    // Not Found
    notFoundTitle: '404',
    notFoundSubtitle: 'Oops! Page not found',
    notFoundDescription: 'The link may be broken or the page has been removed. Try one of the options below:',
    returnToHome: 'Return to Home',
    exploreCatalog: 'Browse Courses',

    // Cart Dropdown
    yourCart: 'Your Cart',
    continueShopping: 'Continue shopping',
    courseWordSingular: 'course',
    courseWordPlural: 'courses',

    // Search Dialog
    searchCoursesLabel: 'Search courses',
    noResultsFor: 'No courses found for',
    resultsCount: 'result',
    resultWord: 'result',
    resultsWord: 'results',

    // Reviews
    reviewsTitle: 'Reviews',
    reviewWord: 'review',
    reviewsWordPlural: 'reviews',
    noReviewsYet: 'No reviews yet.',
    rateCourse: 'Rate course',
    rateModalTitle: 'Rate course',
    clickToRate: 'Click to rate',
    starsOf: 'of 5 stars',
    leaveComment: 'Leave a comment (optional)',
    submitReview: 'Submit review',
    submittingReview: 'Submitting...',
    reviewSuccess: 'Review submitted successfully!',
    reviewError: 'Error submitting review.',
    selectRating: 'Select a rating from 1 to 5 stars.',
    studentDefault: 'Student',

    // Language selector
    portuguese: 'Português',
    english: 'English',
    spanish: 'Español',

    // Contact Page
    contactPageTitle: 'Contact Us',
    contactPageSubtitle: 'Fill out the form below and our team will get back to you shortly.',
    contactName: 'Name',
    contactCompany: 'Company',
    contactRole: 'Job Title',
    contactEmployees: 'Number of Employees',
    contactCoursesInterest: 'Courses of Interest',
    contactOtherCourseLabel: 'Specify the course',
    contactOtherCoursePlaceholder: 'Which course are you looking for?',
    contactEmail: 'Email',
    contactPhone: 'Phone',
    contactMessage: 'Message',
    contactSubmit: 'Send Message',
    contactSubmitting: 'Sending...',
    contactSuccess: 'Message sent!',
    contactSuccessMessage: 'Thank you for reaching out. Our team will get back to you soon.',
    contactError: 'Error sending message. Please try again.',

    // Lesson Player (chrome)
    lessonBack: 'Back',
    videoLesson: 'Video lesson',
    pdfMaterial: 'PDF material',
    quizType: 'Quiz',
    closeContent: 'Close content',
    accessContent: 'Open content',
    lessonCompletedToast: 'Lesson completed!',
    lessonCompleteError: 'Failed to mark lesson as completed.',
    certificateGeneratedToast: '🎉 Certificate generated! Go to the Certificates tab to download it.',
    certificateRequestError: 'Error requesting certificate. Please try again later.',
    concludeCourseError: 'Error completing the course. Please try again.',
    noCourseAccessTitle: "You don't have access to this course",
    noCourseAccessDescription: "You need to be enrolled to access this course's content.",
    previousLesson: 'Previous',
    nextLabel: 'Next',
    markAsCompleted: 'Mark as completed',
    lessonCompletedBadge: 'Completed',
    completeQuizToAdvance: 'Complete the quiz with 70% or more to advance',
    finishCourse: 'Complete course',
    congratsCourseCompleted: 'Congratulations! You completed the course. What did you think?',

    // Lesson Blocks — shared
    howToNavigate: 'How to navigate the course',
    attentionPoint: 'Key point',
    contentInProduction: 'Content in production',
    watchVideo: 'Watch video',
    ofConnector: 'of',
    youGotCorrect: 'You got',
    questionsWord: 'questions',
    classificationsWord: 'classifications',
    matchesWord: 'matches',
    nextLesson: 'Next lesson',
    scoreLabel: 'Score:',
    retakeQuiz: 'Retake quiz',
    tryAgain: 'Try again',

    // Quiz Block
    quizDefaultTitle: 'Quiz',
    activityCompleted: 'Activity completed!',
    keepPracticing: 'Keep practicing',
    excellentWork: 'Excellent work!',
    goodWorkReviewScenarios: 'Good work! Review the scenarios you missed.',
    goodWorkReviewItems: 'Good work! Review the items you missed.',
    reviewConceptsTryAgain: 'Review the concepts and try again to better retain the content.',
    passedCongrats: '🎉 Congratulations! You passed!',
    almostThere: 'Almost there!',
    youNeed: 'You need',
    toPass: 'to pass.',
    youScored: 'You scored',
    generatingCertificate: 'Generating certificate...',
    certificateBeingGenerated: 'Your certificate is being generated and will be emailed to you shortly.',
    confirmAnswer: 'Confirm answer',
    nextQuestion: 'Next question',
    viewResult: 'View result',
    questionLabel: 'Question',
    minimumScore: 'Minimum score:',
    correctLabel: 'Correct!',
    incorrectLabel: 'Incorrect',
    correctAnswerIs: 'The correct answer is:',

    // Classification Quiz Block
    classifyContext: 'Classify the Context',
    yourAnswer: 'Your answer:',
    correctColon: 'Correct:',
    scenarioLabel: 'Scenario:',
    eventLabel: 'EVENT',
    confirmLabel: 'Confirm',
    nextItemLabel: 'Next',

    // Auditor Scenario Block
    hideAnalysis: 'Hide analysis',
    revealAnalysis: 'Reveal analysis',
    scenarioAnalysisLabel: 'SCENARIO ANALYSIS',
    scenarioAnalysisTitle: 'Scenario Analysis',
    internalLabel: 'Internal',
    externalLabel: 'External',
    selectRatingStars: 'Please select a rating from 1 to 5 stars.',
    ratingSubmittedToast: 'Review submitted! 🎉 Certificate available in the Certificates tab.',
    submitRatingError: 'Error submitting review. Please try again.',
    courseCompletedSuccess: 'Congratulations! You successfully completed the course!',
    stepCompleted: 'Correct answer! Step completed.',
    answerCorrectStepDone: 'Your answer is correct. This step was completed successfully.',
    advanceNextLesson: 'Well done! Move on to the next lesson using the button below.',
    reviewQuiz: 'Review quiz',
    whatDidYouThink: 'What did you think of the course?',
    ratingHelperText: 'Your review appears below the completion message and does not close this screen.',
    outOfFiveStars: 'of 5 stars',
    commentPlaceholder: 'Leave a comment (optional)',
    submitRating: 'Submit review',
    thanksForRating: 'Thank you for your review!',
    correctOption: 'Correct',
    incorrectAnswerTryAgain: 'Incorrect answer. Please try again.',

    // Checklist Block
    auditChecklistTitle: 'Audit Checklist',
    verifiedCount: 'checked',
    checklistHelper: 'Click the checkbox to verify, click the question to expand details',
    verifyLabel: 'Check',
    uncheckLabel: 'Uncheck',
    evidenceToRequest: 'Evidence to request',
    checklistCompleteTitle: 'Checklist complete!',
    checklistCompleteMessage: 'You have reviewed all verification points. Use this guide during your real audits.',

    // Matching Block
    termsLabel: 'Terms',
    definitionsLabel: 'Definitions',
    dragDefinitionHere: 'Drag a definition here',
    allDefinitionsMatched: 'All definitions have been matched',
    perfectAllCorrect: '🎉 Perfect! All matches are correct!',
    verifyAnswers: 'Check answers',

    // Scenario Block(s)
    situationLabel: 'Situation',
    questionWordLabel: 'Question',
    viewAnswer: 'View answer',
    hideAnswer: 'Hide answer',
    answerLabel: 'Answer',
    expertTip: 'Expert tip',
    scenarioWord: 'Scenario',

    // PDF / Flip / Media Blocks
    downloadPdfMaterial: 'Download PDF material',
    clickToFlip: 'Click to flip',
    definitionLabel: 'Definition',
    materialInProduction: 'Material in production',
    openMaterial: 'Open material',

    // Sidebar
    collapseLabel: 'Collapse',
    expandLabel: 'Expand',
    moduleLockedTooltip: '🔒 Module locked',
    lockedLabel: 'Locked',
    completedFemPlural: 'completed',
    unlockModuleTooltip: "Complete the previous module's quiz with 70% or more to unlock",

    // Block Error Boundary
    blockRenderErrorTitle: 'This content could not be displayed.',
    blockRenderErrorMessage: 'Please contact support (kaio.souza@dqs.de) — the rest of the lesson remains available below.',
  },

  'es': {
    // Navigation
    home: 'Inicio',
    courses: 'Cursos',
    categories: 'Categorías',
    forCompanies: 'Para Empresas',
    about: 'Acerca de',
    login: 'Iniciar sesión',
    register: 'Registrarse',
    logout: 'Cerrar sesión',
    myCourses: 'Mis Cursos',
    myProfile: 'Mi Perfil',
    helpAndSupport: 'Ayuda y Soporte',
    certificates: 'Certificados',
    search: 'Buscar',
    searchPlaceholder: 'Buscar cursos...',
    faq: 'Preguntas Frecuentes',

    // Categories
    quality: 'Calidad',
    environment: 'Medio Ambiente',
    workSafety: 'Seguridad Laboral',
    infoSecurity: 'Seguridad de la Información',
    automotive: 'Automotriz',
    foodSafety: 'Seguridad Alimentaria',
    technology: 'Tecnología',
    businessContinuity: 'Continuidad del Negocio',
    compliance: 'Cumplimiento',

    // Common
    viewDetails: 'Ver detalles',
    enrollNow: 'Inscríbete ahora',
    startNow: 'Comenzar ahora',
    learnMore: 'Más información',
    cart: 'Carrito',
    emptyCart: 'Tu carrito está vacío',
    goToCheckout: 'Ir al pago',
    searchCourses: 'Buscar cursos',
    remove: 'Eliminar',
    total: 'Total',
    backToHome: 'Volver al inicio',
    backToLogin: 'Volver al inicio de sesión',
    backToCourses: 'Volver a Cursos',
    backToCourse: 'Volver al curso',
    free: 'Gratis',
    loading: 'Cargando...',
    saving: 'Guardando...',
    save: 'Guardar',
    cancel: 'Cancelar',
    close: 'Cerrar',
    or: 'o',
    and: 'y',
    yes: 'Sí',
    no: 'No',

    // Category Pages
    categoryTitle: 'Categoría',
    categoryDescription: 'Explora nuestros cursos de',
    noCourses: 'No se encontraron cursos en esta categoría.',
    categoryNotFound: 'Categoría no encontrada',
    viewAllCategories: 'Ver todas las categorías',
    coursesIn: 'Cursos de',
    comingSoonCategory: 'Próximamente tendremos cursos en esta categoría.',
    exploreOtherCourses: 'Explorar otros cursos',
    viewCourses: 'Ver cursos',

    // Categories Index
    categoriesHeroTitle: 'Categorías',
    categoriesHeroSubtitle: 'Explora nuestros cursos organizados por área de conocimiento. Encuentra capacitaciones especializadas para cada necesidad de tu organización.',
    categoriesNotFoundTitle: '¿No encontraste lo que buscas?',
    categoriesNotFoundSubtitle: 'Explora nuestro catálogo completo con filtros avanzados o contáctanos para soluciones personalizadas.',
    exploreAllCourses: 'Explorar Todos los Cursos',

    // Hero Section
    heroBadge: 'Líder mundial en certificación de sistemas de gestión',
    heroTitle1: 'Desarrolla tus competencias en',
    heroTitle2: 'Sistemas de Gestión',
    heroSubtitle: 'Cursos en línea desarrollados por expertos de DQS, con metodología práctica y certificados reconocidos por el mercado.',
    exploreCourses: 'Explorar Cursos',
    corporateSolutions: 'Soluciones Corporativas',
    availableCourses: 'Cursos Disponibles',
    coursesCompleted: 'Cursos Realizados',
    trainedProfessionals: 'Profesionales Capacitados',
    yearsExperience: 'Años de Experiencia',

    // Featured Courses
    featuredCourses: 'Cursos Destacados',
    featuredCoursesSubtitle: 'Los cursos más buscados por profesionales y empresas',
    viewAllCourses: 'Ver todos los cursos',
    bestSeller: 'Más Vendido',
    newCourse: 'Nuevo',
    hours: 'horas',
    students: 'estudiantes',

    // Why DQS
    whyDQS: '¿Por qué elegir DQS Academy?',
    whyDQSSubtitle: 'La credibilidad de una de las mayores certificadoras del mundo, ahora en una plataforma de educación corporativa.',
    globalExpertise: 'Experiencia Global',
    globalExpertiseDesc: 'Más de 35 años de experiencia en certificación de sistemas de gestión en todo el mundo.',
    specializedInstructors: 'Instructores Especializados',
    specializedInstructorsDesc: 'Auditores y consultores con experiencia práctica en implementación de normas.',
    recognizedCertificates: 'Certificados Reconocidos',
    recognizedCertificatesDesc: 'Certificados con validez nacional emitidos por DQS Academy Brasil.',
    practicalMethodology: 'Metodología Práctica',
    practicalMethodologyDesc: 'Contenido aplicable con estudios de caso y ejercicios del mundo real.',
    continuousSupport: 'Soporte Continuo',
    continuousSupportDesc: 'Acceso a materiales complementarios y soporte técnico especializado.',
    professionalCommunity: 'Comunidad de Profesionales',
    professionalCommunityDesc: 'Networking con miles de profesionales del área de sistemas de gestión.',
    alignedWithStandards: 'Cursos alineados con las principales normas internacionales',
    certified: 'Certificados',

    // Categories Section
    exploreByCategory: 'Explorar por Categoría',
    exploreByCategorySubtitle: 'Encuentra cursos especializados en diversas áreas de sistemas de gestión',
    coursesCount: 'cursos',
    categorySectionQualityDesc: 'ISO 9001, Gestión de Calidad Total, Herramientas de Calidad',
    categorySectionEnvironmentDesc: 'ISO 14001, Gestión Ambiental, Sostenibilidad Corporativa',
    categorySectionSafetyDesc: 'ISO 45001, SST, Prevención de Accidentes',
    categorySectionInfoSecDesc: 'ISO 27001, RGPD, Ciberseguridad',
    categorySectionAutomotiveDesc: 'IATF 16949, VDA, Core Tools',
    categorySectionFoodDesc: 'ISO 22000, FSSC, HACCP',

    // Corporate Section
    corporateSolutionsBadge: 'Soluciones Corporativas',
    corporateTitle: 'Potencia tu equipo con la experiencia de DQS',
    corporateSubtitle: 'Ofrece capacitación de alta calidad para tu organización. Gestiona inscripciones, realiza seguimiento del progreso y desarrolla competencias en sistemas de gestión.',
    teamManagement: 'Gestión de Equipos',
    teamManagementDesc: 'Asigna cursos y realiza seguimiento del progreso de todos los colaboradores.',
    detailedReports: 'Informes Detallados',
    detailedReportsDesc: 'Dashboards con métricas de participación y finalización por equipo.',
    corporateCertificates: 'Certificados Corporativos',
    corporateCertificatesDesc: 'Certificados personalizados con la identidad de tu empresa.',
    learnSolutions: 'Conocer Soluciones',
    talkToConsultant: 'Hablar con un Consultor',
    companiesServed: 'Empresas Atendidas',
    employeesTrained: 'Colaboradores Capacitados',
    satisfaction: 'Satisfacción',
    platformAccess: 'Acceso a la Plataforma',
    exclusivePortal: 'Portal Exclusivo',
    realTimeReports: 'Informes en Tiempo Real',

    // CTA Section
    startYourJourney: 'Comienza tu camino hoy',
    readyToDevelop: '¿Listo para desarrollar tus competencias?',
    ctaSubtitle: 'Únete a miles de profesionales que ya avanzaron en sus carreras con los cursos de DQS Academy Brasil.',
    createFreeAccount: 'Crear Cuenta Gratuita',
    freeSignup: 'Registro gratuito • Acceso inmediato • Sin compromiso',

    // Footer
    footerTagline: 'Excelencia en Capacitación',
    footerDescription: 'Desarrollando competencias en sistemas de gestión con la credibilidad y experiencia de DQS, líder mundial en certificación.',
    coursesTitle: 'Cursos',
    categoriesTitle: 'Categorías',
    institutionalTitle: 'Institucional',
    legalTitle: 'Legal',
    aboutDQS: 'Acerca de DQS Academy',
    contact: 'Contacto',
    termsOfUse: 'Términos de Uso',
    privacyPolicy: 'Política de Privacidad',
    cookiePolicy: 'Política de Cookies',
    allRightsReserved: 'Todos los derechos reservados.',
    companyGroup: 'Una empresa del grupo',

    // Auth — Login
    welcomeBack: 'Bienvenido de nuevo',
    loginSubtitle: 'Inicia sesión en tu cuenta para continuar',
    email: 'Correo electrónico',
    password: 'Contraseña',
    forgotPassword: '¿Olvidaste tu contraseña?',
    noAccount: '¿No tienes cuenta?',
    createAccount: 'Crear cuenta',
    loginTab: 'Iniciar sesión',
    createAccountTab: 'Crear Cuenta',
    loggingIn: 'Iniciando sesión...',
    loginButton: 'Iniciar sesión',
    orContinueWith: 'o continúa con',
    invalidCredentials: 'Correo electrónico o contraseña inválidos',
    passwordsDoNotMatch: 'Las contraseñas no coinciden',
    passwordTooShort: 'La contraseña debe contener al menos 8 caracteres, incluyendo letras mayúsculas, minúsculas y números.',
    accountCreatedSuccess: '¡Cuenta creada! Revisa tu correo electrónico para confirmar el registro.',
    accountCreatedError: 'Error al crear la cuenta. Inténtalo de nuevo.',

    // Auth — Register
    createAccountTitle: 'Crear tu cuenta',
    registerSubtitle: 'Regístrate para acceder a nuestros cursos',
    fullName: 'Nombre completo',
    yourName: 'Tu nombre',
    confirmPassword: 'Confirmar contraseña',
    repeatPassword: 'Repite la contraseña',
    alreadyHaveAccount: '¿Ya tienes una cuenta?',
    creatingAccount: 'Creando cuenta...',
    termsAgreement: 'He leído y acepto los',
    marketingConsent: 'Deseo recibir novedades y ofertas por correo electrónico',
    minCharsPassword: 'Mínimo 6 caracteres',
    cpf: 'CPF',
    invalidCpf: 'CPF inválido',

    // Auth — Register (Cadastrar page)
    joinDQSAcademy: 'Únete a DQS Academy',
    joinSubtitle: 'Crea tu cuenta y accede a los mejores cursos en sistemas de gestión desarrollados por expertos de DQS.',
    benefitHighQuality: 'Acceso a cursos de alta calidad',
    benefitInternationalCerts: 'Certificados reconocidos internacionalmente',
    benefitExpertContent: 'Contenido desarrollado por expertos DQS',
    benefitSupport: 'Soporte técnico y pedagógico',
    accountCreatedTitle: '¡Cuenta creada con éxito!',
    confirmEmailSent: 'Enviamos un correo de confirmación a',
    checkSpam: '¿No lo recibiste? Revisa la carpeta de spam.',
    resendEmail: 'Reenviar correo de confirmación',
    resendingEmail: 'Reenviando...',
    fillAllFields: 'Por favor, completa todos los campos.',
    fillYourData: 'Completa tus datos para comenzar',
    emailResent: '¡Correo reenviado!',
    emailResendError: 'Error al reenviar correo',
    acceptTermsRequired: 'Debes aceptar los términos de uso para continuar.',

    // Auth — Recovery
    recoverPassword: 'Recuperar contraseña',
    recoverPasswordSubtitle: 'Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.',
    sendRecoveryLink: 'Enviar enlace de recuperación',
    sendingEmail: 'Enviando...',
    emailSent: '¡Correo enviado!',
    recoverySentMessage: 'Enviamos un enlace de recuperación a',
    sendError: 'Error al enviar correo',

    // Auth — Reset Password
    resetPassword: 'Restablecer contraseña',
    resetPasswordSubtitle: 'Elige una nueva contraseña para tu cuenta.',
    newPassword: 'Nueva contraseña',
    confirmNewPassword: 'Confirmar nueva contraseña',
    resetButton: 'Restablecer contraseña',
    resettingPassword: 'Guardando...',
    passwordResetSuccess: '¡Contraseña restablecida!',
    passwordResetSuccessMessage: 'Tu contraseña ha sido cambiada con éxito. Redirigiendo al inicio de sesión...',
    passwordResetError: 'Error al restablecer contraseña',

    // Student Area
    studentArea: 'Área del Estudiante',
    myCoursesTab: 'Mis Cursos',
    profileTab: 'Mi Perfil',
    certificatesTab: 'Certificados',
    libraryTab: 'Biblioteca',
    libraryInfoTitle: 'Materiales de apoyo',
    libraryInfoSubtitle: 'Manuales, ebooks y diapositivas de las capacitaciones internas, para consultar cuando lo necesites.',
    libraryInternalBadge: 'Uso interno DQS',
    libraryEmpty: 'Todavía no hay material publicado',
    libraryEmptySubtitle: 'Cuando se publiquen los materiales de las capacitaciones, aparecerán aquí.',
    libraryOpenFile: 'Descargar',
    libraryOpenLink: 'Abrir',
    aiDisclosureLesson: 'Imagen y voz del presentador generadas por IA. Contenido técnico creado y revisado por especialistas DQS en la norma de este curso.',
    aiDisclosureDocument: 'Material producido con apoyo de IA y revisado por especialistas DQS en la norma de este curso.',
    aiDisclosurePreview: 'Vídeos con imagen y voz generadas por IA. Materiales producidos con apoyo de IA. Todo el contenido técnico es creado y revisado por especialistas DQS en la norma del curso.',
    aiDisclosurePreviewNoVideo: 'Materiales producidos con apoyo de IA. Todo el contenido técnico es creado y revisado por especialistas DQS en la norma del curso.',
    aiDisclosureAriaLabel: 'Aviso sobre el uso de inteligencia artificial',
    hello: 'Hola',
    welcomeToLearning: 'Bienvenido a tu área de aprendizaje',

    // My Courses
    inProgress: 'En progreso',
    completed: 'Completado',
    notStarted: 'No iniciado',
    continueWatching: 'Continuar',
    startCourse: 'Iniciar',
    reviewCourse: 'Revisar Curso',
    enrolledCourses: 'Cursos inscritos',
    yourCourses: 'Tus cursos',
    noCoursesYet: 'Aún no estás inscrito en ningún curso.',
    noCoursesSubtitle: 'Explora nuestro catálogo y comienza a aprender.',
    lessons: 'lecciones',
    lastAccessed: 'Último acceso:',
    today: 'Hoy',
    yesterday: 'Ayer',
    daysAgo: 'días atrás',
    percentCompleted: 'completado',

    // My Certificates
    downloadCertificate: 'Descargar PDF',
    viewCertificate: 'Ver',
    noCertificatesYet: 'Aún no hay certificados',
    noCertificatesSubtitle: 'Completa un curso para recibir tu certificado.',
    certificatesInfoTitle: 'Certificados DQS Academy',
    certificatesInfoSubtitle: 'Los certificados se emiten automáticamente al completar el 100% del curso. Cada certificado tiene un código único de validación que puede verificarse en nuestro portal.',
    issuedOn: 'Emitido el:',
    verificationCode: 'Código:',
    downloadPdf: 'Descargar PDF',
    certificateValidation: 'Validación de Certificados',
    certificateValidationDesc: 'Todos los certificados emitidos por DQS Academy Brasil pueden validarse a través del código único presente en el documento.',

    // My Profile
    personalInfo: 'Información Personal',
    personalInfoSubtitle: 'Tus datos de registro básicos',
    professionalInfo: 'Información Profesional',
    professionalInfoSubtitle: 'Datos de tu empresa y cargo (opcional)',
    preferences: 'Preferencias',
    security: 'Seguridad',
    securitySubtitle: 'Gestiona tu contraseña y seguridad de la cuenta',
    company: 'Empresa',
    companyPlaceholder: 'Nombre de la empresa',
    position: 'Cargo',
    positionPlaceholder: 'Tu cargo actual',
    fieldOfWork: 'Área de actuación',
    fieldOfWorkPlaceholder: 'Ej: Gestión de Calidad',
    areaOfInterest: 'Área de interés principal',
    selectArea: 'Selecciona un área',
    changePassword: 'Cambiar contraseña',
    changePasswordSubtitle: 'Cambia tu contraseña de acceso',
    currentPassword: 'Contraseña actual',
    saveChanges: 'Guardar cambios',
    savingChanges: 'Guardando...',
    profileSaved: '¡Perfil actualizado con éxito!',
    profileSaveError: 'Error al guardar',
    passwordChanged: '¡Contraseña cambiada con éxito!',
    passwordChangeError: 'Error al cambiar contraseña',
    currentPasswordIncorrect: 'Contraseña actual incorrecta',
    accountStatus: 'Estado de la Cuenta',
    accountStatusSubtitle: 'Información sobre tu cuenta',
    registrationDate: 'Fecha de registro',
    accessType: 'Tipo de acceso',
    status: 'Estado',
    active: 'Activo',
    roleStudent: 'Estudiante',
    roleAdmin: 'Administrador',
    roleModerator: 'Moderador',
    contactSupport: 'Contacta al soporte si es necesario',
    passwordDialogTitle: 'Cambiar contraseña',
    passwordDialogSubtitle: 'Ingresa tu contraseña actual y la nueva contraseña deseada.',
    changingPassword: 'Cambiando...',

    interestQuality: 'Calidad (ISO 9001)',
    interestEnvironment: 'Medio Ambiente (ISO 14001)',
    interestSafety: 'Salud y Seguridad (ISO 45001)',
    interestIntegrated: 'Sistema de Gestión Integrado',
    interestInfoSec: 'Seguridad de la Información (ISO 27001)',
    interestAudit: 'Auditoría',
    interestOther: 'Otros',

    // Cursos (Catalog)
    catalogTitle: 'Catálogo de Cursos',
    catalogSubtitle: 'Explora nuestra selección de cursos en sistemas de gestión, desarrollados por expertos de DQS con metodología práctica y certificados reconocidos.',
    searchCoursesPlaceholder: 'Buscar cursos...',
    sortRelevance: 'Relevancia',
    sortPopular: 'Más Populares',
    sortTopRated: 'Mejor Valorados',
    sortLowestPrice: 'Menor Precio',
    sortHighestPrice: 'Mayor Precio',
    coursesFound: 'curso',
    courseWord: 'curso',
    coursesWord: 'cursos',
    noCoursesFound: 'No se encontraron cursos',
    noCoursesFoundSubtitle: 'Intenta ajustar los filtros o realizar una nueva búsqueda.',
    loadError: 'Error al cargar cursos. Inténtalo de nuevo más tarde.',
    continueCourse: 'Continuar curso',
    viewCertificate: 'Ver Certificado',
    enrollErrorTitle: 'Error al iniciar el curso',
    enrollErrorDescription: 'Inténtalo de nuevo.',

    // Course Filters
    filters: 'Filtros',
    clearFilters: 'Limpiar',
    filterCategory: 'Categoría',
    filterStandard: 'Norma / Estándar',

    // Course Detail
    backToCatalog: 'Volver a Cursos',
    reviews: 'reseñas',
    reviewsCount: 'reseñas',
    evaluations: 'evaluaciones',
    ofContent: 'de contenido',
    lessonsCount: 'lecciones',
    moduleWord: 'módulo',
    modulesWord: 'módulos',
    coursePreview: 'Vista previa del curso',
    progress: 'Progreso',
    accessCourse: 'Acceder al curso',
    buyNow: 'Comprar Ahora',
    immediateAccess: 'Acceso inmediato',
    certificateIncluded: 'Certificado incluido',
    courseIncludes: 'Este curso incluye:',
    videoLessons: 'lecciones en vídeo',
    completionCertificate: 'Certificado de finalización',
    oneYearAccess: '1 año de acceso',
    whatYouWillLearn: 'Lo que aprenderás',
    courseContent: 'Contenido del curso',
    totalDuration: 'de duración total',
    forWhomTitle: '¿Para quién es este curso?',
    prerequisites: 'Requisitos previos',
    includedMaterials: 'Materiales incluidos',
    readyToStart: '¿Listo para comenzar tu camino?',
    readyToStartSubtitle: 'Invierte en tu desarrollo profesional y destácate en el mercado.',
    viewOtherCourses: 'Ver otros cursos',
    paymentConfirmed: '¡Pago confirmado! Bienvenido al curso 🎉',
    paymentError: 'Error al iniciar el pago. Inténtalo de nuevo.',
    noPaymentUrl: 'No se devolvió una URL de pago. Verifica la configuración de Stripe.',
    serverError: 'Error al conectar con el servidor de pagos.',
    installmentsOf: 'o hasta',
    videoType: 'Vídeo',

    // Checkout
    payment: 'Pago',
    cardTab: 'Tarjeta',
    pixTab: 'PIX',
    boletoTab: 'Boleto',
    cardNumber: 'Número de tarjeta',
    cardName: 'Nombre en la tarjeta',
    cardNamePlaceholder: 'Como aparece impreso en la tarjeta',
    expiry: 'Vencimiento',
    cvv: 'CVV',
    installments: 'Cuotas',
    installmentSingle: 'al contado',
    installmentMultiple: 'x de R$',
    finishPayment: 'Finalizar pago',
    processingPayment: 'Procesando pago...',
    originalPrice: 'Precio original',
    securePurchase: 'Compra segura',
    sslCertificate: 'Certificado SSL',
    sevenDayGuarantee: '7 días de garantía',
    scanQrCode: 'Escanea el código QR con tu app bancaria',
    codeExpiresIn: 'Este código expira en',
    copyPixKey: 'Copiar clave PIX',
    boletoExpiresIn: 'El boleto vence en',
    generateBoleto: 'Generar boleto',
    boletoConfirmation: 'Después del pago, la confirmación puede tardar hasta 3 días hábiles.',
    comingSoon: '¡Próximamente! Integración con Stripe en progreso.',
    comingSoonBadge: 'Próximamente',

    // Checkout Success
    paymentConfirmedTitle: '¡Pago confirmado!',
    paymentProcessingSubtitle: 'Tu matrícula está siendo procesada. En instantes tendrás acceso al curso.',
    goToMyCourses: 'Ir a Mis Cursos',
    validationFailed: 'Revisa el formulario',
    processing: 'Procesando...',
    startCourseNow: 'Comenzar el curso ahora',
    whatHappensNext: '¿Qué pasa ahora?',
    nextStepAccessTitle: 'Acceso liberado inmediatamente',
    nextStepAccessDesc: 'Tu inscripción está activa. Puedes comenzar a estudiar ahora mismo.',
    nextStepEmailTitle: 'Email de confirmación enviado',
    nextStepEmailDesc: 'En pocos minutos recibirás la factura y el enlace de acceso por email.',
    nextStepStudyTitle: 'Estudia a tu ritmo',
    nextStepStudyDesc: 'Acceso vitalicio. Pausa y reanuda cuando quieras, en cualquier dispositivo.',
    nextStepCertTitle: 'Certificado automático',
    nextStepCertDesc: 'Al completar el 100% de las lecciones, el certificado estará disponible en tu área de alumno.',
    checkoutSuccessSupport: '¿Alguna duda?',
    viewCourse: 'Ver el curso',

    // FAQ
    faqTitle: 'Preguntas Frecuentes',
    faqSubtitle: 'Encuentra respuestas a las preguntas más comunes sobre nuestros cursos, certificados, pagos y soluciones corporativas.',
    faqCategoryCoursesTitle: 'Cursos',
    faqCategoryCertificatesTitle: 'Certificados',
    faqCategoryPaymentTitle: 'Pago',
    faqCategoryCompaniesTitle: 'Empresas',
    faqNotFoundTitle: '¿No encontraste tu respuesta?',
    faqNotFoundSubtitle: 'Nuestro equipo está listo para ayudarte. Contáctanos y con gusto aclararemos tus dudas.',
    faqContactButton: 'Contactar Soporte',
    faqExploreButton: 'Explorar Cursos',
    faqCourseQ1: '¿Qué tipos de cursos ofrece DQS Academy?',
    faqCourseA1: 'Ofrecemos cursos en diversas áreas de sistemas de gestión, incluyendo Calidad (ISO 9001), Medio Ambiente (ISO 14001), Seguridad Laboral (ISO 45001), Seguridad de la Información (ISO 27001) y Automotriz (IATF 16949). Los cursos están disponibles en formatos e-learning, en vivo e híbrido.',
    faqCourseQ2: '¿Cuál es la duración promedio de los cursos?',
    faqCourseA2: 'La duración varía según el curso y nivel de profundidad. Los cursos introductorios suelen tener entre 8 y 16 horas, mientras que las formaciones más completas pueden alcanzar 32 horas o más. La carga horaria siempre está indicada en la página de cada curso.',
    faqCourseQ3: '¿Los cursos están actualizados?',
    faqCourseA3: 'Sí, todos nuestros cursos son desarrollados y revisados por auditores y expertos de DQS, garantizando que el contenido esté siempre alineado con las versiones más recientes de las normas y mejores prácticas del mercado.',
    faqCourseQ4: '¿Puedo acceder al contenido después de completar el curso?',
    faqCourseA4: 'Sí, mantienes acceso al contenido del curso durante 12 meses después de la inscripción. Esto te permite revisar el material cuando sea necesario.',
    faqCertQ1: '¿Cómo funciona la emisión del certificado?',
    faqCertA1: 'Al completar el 100% de las lecciones y alcanzar la nota mínima en las evaluaciones (cuando corresponda), el certificado se emite automáticamente y queda disponible para descarga en tu área de estudiante.',
    faqCertQ2: '¿El certificado tiene validez internacional?',
    faqCertA2: 'Sí, los certificados de DQS Academy son reconocidos internacionalmente, ya que llevan el sello de DQS, una de las certificadoras más respetadas del mundo con presencia en más de 60 países.',
    faqCertQ3: '¿El certificado es válido para comprobación profesional?',
    faqCertA3: 'Sí, nuestros certificados son válidos para comprobar capacitación profesional y pueden utilizarse en procesos de selección, auditorías y para cumplir requisitos de competencia.',
    faqPayQ1: '¿Qué formas de pago se aceptan?',
    faqPayA1: 'Aceptamos tarjeta de crédito (hasta 12 cuotas), boleto bancario y PIX. Para compras corporativas, también ofrecemos facturación directa.',
    faqPayQ2: '¿Puedo solicitar un reembolso?',
    faqPayA2: 'Sí, ofrecemos una garantía de 7 días. Si no estás satisfecho con el curso, puedes solicitar el reembolso completo dentro de ese plazo, siempre que no hayas completado más del 30% del contenido.',
    faqPayQ3: '¿Hay descuentos por compra de múltiples cursos?',
    faqPayA3: 'Sí, ofrecemos descuentos progresivos por compras múltiples y condiciones especiales para empresas. Contacta a nuestro equipo comercial para una propuesta personalizada.',
    faqPayQ4: '¿El precio incluye todos los materiales?',
    faqPayA4: 'Sí, el valor del curso incluye acceso completo a las lecciones, materiales complementarios en PDF, ejercicios prácticos y certificado de finalización.',
    faqCompQ1: '¿Cómo funciona la capacitación corporativa?',
    faqCompA1: 'Ofrecemos soluciones personalizadas para empresas, incluyendo plataforma white-label, gestión de equipos, informes de progreso y rutas de aprendizaje personalizadas. Contáctanos para una propuesta a medida.',
    faqCompQ2: '¿Puedo emitir factura a nombre de la empresa?',
    faqCompA2: 'Sí, todas las compras pueden facturarse a nombre de la empresa. Solo necesitas proporcionar los datos de la empresa al momento de la compra o contactar a nuestro equipo de soporte.',
    faqCompQ3: '¿Hay un número mínimo de colaboradores?',
    faqCompA3: 'No hay un mínimo obligatorio, pero ofrecemos condiciones especiales a partir de 5 inscripciones. Para grupos más grandes, desarrollamos propuestas personalizadas con beneficios exclusivos.',
    faqCompQ4: '¿Puedo seguir el progreso de mi equipo?',
    faqCompA4: 'Sí, en el plan corporativo tienes acceso a un panel administrativo con informes de progreso, tasas de finalización y rendimiento de cada colaborador.',

    // About
    aboutHeroTitle: 'Acerca de DQS Academy Brasil',
    aboutHeroSubtitle: 'La excelencia de DQS en certificación, ahora aplicada al desarrollo de competencias para profesionales y organizaciones en todo Brasil.',
    whoWeAre: 'Quiénes Somos',
    whoWeAreP1: 'DQS Academy Brasil es el brazo educativo de DQS, una de las mayores y más respetadas certificadoras del mundo. Con más de 40 años de experiencia global en auditoría y certificación de sistemas de gestión, traemos este conocimiento para formar profesionales de excelencia.',
    whoWeAreP2: 'Nuestros cursos son desarrollados por auditores y especialistas que viven diariamente la aplicación práctica de las normas en las más diversas organizaciones y sectores de la economía.',
    whoWeAreP3: 'Creemos que el conocimiento es el camino hacia la transformación. Por eso, nos dedicamos a ofrecer capacitaciones que realmente marcan la diferencia en la carrera de los profesionales y en los resultados de las organizaciones.',
    yearsGlobalExperience: 'Años de Experiencia Global',
    partOfGroup: 'Parte del Grupo DQS',
    partOfGroupSubtitle: 'DQS es una de las organizaciones de certificación más experimentadas del mundo, con presencia en más de 60 países y una red de miles de auditores calificados atendiendo a clientes de todos los tamaños y sectores.',
    countries: 'Países',
    clients: 'Clientes',
    auditors: 'Auditores',
    standards: 'Normas',
    ourValues: 'Nuestros Valores',
    ourValuesSubtitle: 'Los principios que guían nuestra actuación y nuestro compromiso contigo.',
    valueExcellence: 'Excelencia',
    valueExcellenceDesc: 'Compromiso con los más altos estándares de calidad en todo lo que hacemos.',
    valuePartnership: 'Alianza',
    valuePartnershipDesc: 'Construimos relaciones a largo plazo basadas en confianza y resultados.',
    valueKnowledge: 'Conocimiento',
    valueKnowledgeDesc: 'Compartimos experiencia para desarrollar profesionales y organizaciones.',
    valueGlobalization: 'Globalización',
    valueGlobalizationDesc: 'Presencia internacional con actuación local adaptada a cada mercado.',
    ourTrajectory: 'Nuestra Trayectoria',
    milestone1985: 'Fundación de DQS en Alemania',
    milestone1990: 'Expansión a mercados internacionales',
    milestone1994: 'Inicio de operaciones en Brasil',
    milestone2010: 'Expansión de capacitaciones y auditorías en Brasil',
    milestone2025: 'Creación de DQS Academy Brasil',
    milestoneToday: 'Líder en capacitación en sistemas de gestión',
    milestoneLabel: 'Hoy',
    educationalDifferential: 'Diferencial Educativo',
    educationalDifferentialP1: 'Nuestro diferencial radica en la combinación única entre conocimiento técnico profundo y experiencia práctica de auditoría. Todos nuestros instructores son profesionales activos que conocen los desafíos reales de las organizaciones.',
    educationalDifferentialP2: 'Los contenidos se actualizan constantemente para reflejar los últimos cambios normativos y las mejores prácticas del mercado, garantizando que siempre aprendas lo más relevante.',
    whyDQSAcademy: '¿Por qué DQS Academy?',
    diffContent: 'Contenido desarrollado por auditores experimentados',
    diffMethodology: 'Metodología enfocada en la práctica',
    diffCertificates: 'Certificados con reconocimiento internacional',
    diffSupport: 'Soporte técnico especializado',
    diffUpdated: 'Actualización continua de materiales',
    aboutCtaTitle: 'Comienza tu camino de desarrollo',
    aboutCtaSubtitle: 'Explora nuestros cursos y descubre cómo DQS Academy puede impulsar tu carrera y tu organización.',

    // Para Empresas
    companiesHeroBadge: 'Soluciones para Empresas',
    companiesHeroTitle: 'Capacitación Corporativa con Excelencia DQS',
    companiesHeroSubtitle: 'Desarrolla competencias estratégicas en tu equipo con los mayores expertos en sistemas de gestión.',
    requestContact: 'Solicitar Contacto',
    useCasesTitle: 'Casos de Uso',
    useCasesSubtitle: 'Descubre cómo DQS Academy puede apoyar los objetivos de capacitación de tu organización.',
    useCaseCertTitle: 'Certificación de Equipos',
    useCaseCertDesc: 'Prepara a tu equipo para auditorías de certificación con capacitaciones oficiales DQS.',
    useCaseDevTitle: 'Desarrollo Continuo',
    useCaseDevDesc: 'Mantén a tu equipo actualizado con las últimas versiones de las normas ISO.',
    useCaseComplianceTitle: 'Cumplimiento y Conformidad',
    useCaseComplianceDesc: 'Asegura que todos los colaboradores conozcan los requisitos normativos aplicables.',
    useCaseOnboardingTitle: 'Integración de Nuevos Colaboradores',
    useCaseOnboardingDesc: 'Integra a los nuevos miembros con capacitaciones estandarizadas sobre sistemas de gestión.',
    differentialsTitle: 'Diferenciales DQS Academy',
    differentialsSubtitle: 'Por qué los líderes del mercado eligen DQS para capacitar a sus equipos.',
    diffGlobalExpertise: 'Experiencia Global',
    diffGlobalExpertiseDesc: 'Más de 40 años de experiencia en certificación y capacitación en sistemas de gestión.',
    diffUpdatedContent: 'Contenido Actualizado',
    diffUpdatedContentDesc: 'Cursos desarrollados y revisados por auditores activos de DQS.',
    diffPracticalMethod: 'Metodología Práctica',
    diffPracticalMethodDesc: 'Enfoque en aplicación real con estudios de caso y ejercicios prácticos.',
    diffB2B: 'B2B & E-Learning',
    diffB2BDesc: 'Plataforma 100% en línea desarrollada para empresas que buscan capacitar a sus equipos en normas y sistemas de gestión.',
    diffDedicatedSupport: 'Soporte Dedicado',
    diffDedicatedSupportDesc: 'Equipo de atención corporativa para gestionar tu cuenta.',
    diffRecognizedCert: 'Certificación Reconocida',
    diffRecognizedCertDesc: 'Certificados con el sello DQS, aceptados internacionalmente.',
    companiesCtaTitle: '¿Listo para elevar el nivel de tu equipo?',
    companiesCtaSubtitle: 'Nuestro equipo está listo para entender tus necesidades y proponer la mejor solución de capacitación para tu empresa.',
    talkToSpecialist: 'Hablar con un Especialista',
    viewCompleteSolutions: 'Ver Soluciones Completas',

    // Soluções Corporativas
    corpHeroTitle: 'Soluciones Corporativas en Capacitación',
    corpHeroSubtitle: 'Transforma el conocimiento de tu equipo en resultados. DQS Academy ofrece programas de capacitación empresarial con la credibilidad de más de 40 años de experiencia global.',
    talkToConsultantBtn: 'Hablar con un Consultor',
    knowCourses: 'Conocer Cursos',
    corpBenefitsTitle: '¿Por qué elegir DQS Academy?',
    corpBenefitsSubtitle: 'Una plataforma completa para gestión de capacitaciones corporativas con la calidad y confiabilidad que tu empresa necesita.',
    corpBenefitTeamTitle: 'Gestión de Equipos',
    corpBenefitTeamDesc: 'Administra el acceso y progreso de todos los colaboradores en una única plataforma centralizada.',
    corpBenefitReportsTitle: 'Informes y Dashboards',
    corpBenefitReportsDesc: 'Acompaña KPIs de capacitación, tasas de finalización y rendimiento en tiempo real.',
    corpBenefitTrailsTitle: 'Rutas Personalizadas',
    corpBenefitTrailsDesc: 'Crea recorridos de aprendizaje personalizados para cada función o departamento de tu empresa.',
    corpBenefitCertsTitle: 'Certificados Corporativos',
    corpBenefitCertsDesc: 'Emite certificados validados por DQS con la identidad visual de tu organización.',
    corpHowItWorks: 'Cómo Funciona',
    corpHowItWorksSubtitle: 'Un proceso simple y escalable para desarrollar competencias con capacitaciones certificadas.',
    corpStep1Title: 'Diagnóstico',
    corpStep1Desc: 'Entendemos las necesidades de capacitación de tu organización.',
    corpStep2Title: 'Propuesta Personalizada',
    corpStep2Desc: 'Desarrollamos un plan de capacitación a medida para tu equipo.',
    corpStep3Title: 'Liberación de Accesos',
    corpStep3Desc: 'Registra a los colaboradores e inicia el seguimiento inmediato de la capacitación en la plataforma.',
    corpStep4Title: 'Seguimiento',
    corpStep4Desc: 'Soporte continuo e informes de evolución para garantizar resultados.',
    corpMetricCompanies: 'Empresas Atendidas',
    corpMetricProfessionals: 'Profesionales Capacitados',
    corpMetricSatisfaction: 'Tasa de Satisfacción',
    corpMetricExperience: 'Años de Experiencia DQS',
    corpFeaturesTitle: 'Todo lo que tu empresa necesita en un solo lugar',
    corpFeature1: 'Acceso corporativo a DQS Academy para capacitación certificable',
    corpFeature1Desc: 'Centraliza el desarrollo de competencias de tu equipo en una única plataforma',
    corpFeature2: 'Cursos desarrollados por expertos DQS',
    corpFeature3: 'Soporte técnico y pedagógico dedicado',
    corpFeature4: 'Acceso móvil para aprender en cualquier lugar',
    corpFeature5: 'Contenido actualizado según normas vigentes',
    corpFeature6: 'Certificados con validez internacional',
    corpReadyToStart: '¿Listo para comenzar?',
    corpReadySubtitle: 'Contacta a nuestro equipo comercial y recibe una propuesta personalizada para las necesidades de tu organización.',
    requestProposal: 'Solicitar Propuesta',
    corpServiceBrazil: 'Atención en todo Brasil',

    // Verificar Certificado
    certVerificationTitle: 'DQS Academy Brasil',
    certVerificationSubtitle: 'Verificación de Certificado',
    verifyingCert: 'Verificando certificado…',
    certValid: 'Certificado Válido',
    certValidSubtitle: 'Este certificado fue emitido por DQS Academy Brasil y es auténtico.',
    certNotFound: 'Certificado no encontrado',
    certNotFoundSubtitle: 'El código proporcionado no corresponde a ningún certificado emitido por DQS Academy Brasil. Verifica que el código sea correcto e inténtalo de nuevo.',
    certStudentLabel: 'Estudiante',
    certCourseLabel: 'Curso',
    certIssueDateLabel: 'Fecha de Emisión',
    certCodeLabel: 'Código de Verificación',
    backToSite: 'Volver al sitio',

    // Not Found
    notFoundTitle: '404',
    notFoundSubtitle: '¡Ups! Página no encontrada',
    notFoundDescription: 'El enlace puede estar roto o la página fue eliminada. Prueba una de las opciones:',
    returnToHome: 'Volver al Inicio',
    exploreCatalog: 'Explorar Cursos',

    // Cart Dropdown
    yourCart: 'Tu Carrito',
    continueShopping: 'Continuar comprando',
    courseWordSingular: 'curso',
    courseWordPlural: 'cursos',

    // Search Dialog
    searchCoursesLabel: 'Buscar cursos',
    noResultsFor: 'No se encontraron cursos para',
    resultsCount: 'resultado',
    resultWord: 'resultado',
    resultsWord: 'resultados',

    // Reviews
    reviewsTitle: 'Reseñas',
    reviewWord: 'reseña',
    reviewsWordPlural: 'reseñas',
    noReviewsYet: 'Aún no hay reseñas.',
    rateCourse: 'Evaluar curso',
    rateModalTitle: 'Evaluar curso',
    clickToRate: 'Haz clic para evaluar',
    starsOf: 'de 5 estrellas',
    leaveComment: 'Deja un comentario (opcional)',
    submitReview: 'Enviar evaluación',
    submittingReview: 'Enviando...',
    reviewSuccess: '¡Evaluación enviada con éxito!',
    reviewError: 'Error al enviar evaluación.',
    selectRating: 'Selecciona una calificación de 1 a 5 estrellas.',
    studentDefault: 'Estudiante',

    // Language selector
    portuguese: 'Português',
    english: 'English',
    spanish: 'Español',

    // Contact Page
    contactPageTitle: 'Contáctenos',
    contactPageSubtitle: 'Complete el formulario a continuación y nuestro equipo se comunicará con usted pronto.',
    contactName: 'Nombre',
    contactCompany: 'Empresa',
    contactRole: 'Cargo',
    contactEmployees: 'Número de Colaboradores',
    contactCoursesInterest: 'Cursos de Interés',
    contactOtherCourseLabel: 'Especifique el curso',
    contactOtherCoursePlaceholder: '¿Qué curso busca?',
    contactEmail: 'Correo electrónico',
    contactPhone: 'Teléfono',
    contactMessage: 'Mensaje',
    contactSubmit: 'Enviar Mensaje',
    contactSubmitting: 'Enviando...',
    contactSuccess: '¡Mensaje enviado!',
    contactSuccessMessage: 'Gracias por contactarnos. Nuestro equipo se comunicará pronto.',
    contactError: 'Error al enviar el mensaje. Inténtelo de nuevo.',

    // Lesson Player (chrome)
    lessonBack: 'Volver',
    videoLesson: 'Videolección',
    pdfMaterial: 'Material en PDF',
    quizType: 'Cuestionario',
    closeContent: 'Cerrar contenido',
    accessContent: 'Abrir contenido',
    lessonCompletedToast: '¡Lección completada!',
    lessonCompleteError: 'Error al marcar la lección como completada.',
    certificateGeneratedToast: '🎉 ¡Certificado generado! Ve a la pestaña Certificados para descargarlo.',
    certificateRequestError: 'Error al solicitar el certificado. Inténtelo de nuevo más tarde.',
    concludeCourseError: 'Error al finalizar el curso. Inténtelo de nuevo.',
    noCourseAccessTitle: 'No tienes acceso a este curso',
    noCourseAccessDescription: 'Necesitas estar inscrito para acceder al contenido de este curso.',
    previousLesson: 'Anterior',
    nextLabel: 'Siguiente',
    markAsCompleted: 'Marcar como completada',
    lessonCompletedBadge: 'Completada',
    completeQuizToAdvance: 'Completa el cuestionario con 70% o más para avanzar',
    finishCourse: 'Finalizar curso',
    congratsCourseCompleted: '¡Felicidades! Has completado el curso. ¿Qué te pareció?',

    // Lesson Blocks — shared
    howToNavigate: 'Cómo navegar por el curso',
    attentionPoint: 'Punto de atención',
    contentInProduction: 'Contenido en producción',
    watchVideo: 'Ver vídeo',
    ofConnector: 'de',
    youGotCorrect: 'Acertaste',
    questionsWord: 'preguntas',
    classificationsWord: 'clasificaciones',
    matchesWord: 'asociaciones',
    nextLesson: 'Siguiente lección',
    scoreLabel: 'Puntuación:',
    retakeQuiz: 'Repetir cuestionario',
    tryAgain: 'Intentar de nuevo',

    // Quiz Block
    quizDefaultTitle: 'Cuestionario',
    activityCompleted: '¡Actividad completada!',
    keepPracticing: 'Sigue practicando',
    excellentWork: '¡Excelente trabajo!',
    goodWorkReviewScenarios: '¡Buen trabajo! Revisa los escenarios que fallaste.',
    goodWorkReviewItems: '¡Buen trabajo! Revisa los ítems que fallaste.',
    reviewConceptsTryAgain: 'Revisa los conceptos e inténtalo de nuevo para afianzar mejor el contenido.',
    passedCongrats: '🎉 ¡Felicidades! ¡Aprobaste!',
    almostThere: '¡Casi lo logras!',
    youNeed: 'Necesitas',
    toPass: 'para aprobar.',
    youScored: 'Obtuviste',
    generatingCertificate: 'Generando certificado...',
    certificateBeingGenerated: 'Tu certificado se está generando y se enviará por correo en breve.',
    confirmAnswer: 'Confirmar respuesta',
    nextQuestion: 'Siguiente pregunta',
    viewResult: 'Ver resultado',
    questionLabel: 'Pregunta',
    minimumScore: 'Nota mínima:',
    correctLabel: '¡Correcto!',
    incorrectLabel: 'Incorrecto',
    correctAnswerIs: 'La respuesta correcta es:',

    // Classification Quiz Block
    classifyContext: 'Clasifica el Contexto',
    yourAnswer: 'Tu respuesta:',
    correctColon: 'Correcto:',
    scenarioLabel: 'Escenario:',
    eventLabel: 'EVENTO',
    confirmLabel: 'Confirmar',
    nextItemLabel: 'Siguiente',

    // Auditor Scenario Block
    hideAnalysis: 'Ocultar análisis',
    revealAnalysis: 'Revelar análisis',
    scenarioAnalysisLabel: 'ANÁLISIS DEL ESCENARIO',
    scenarioAnalysisTitle: 'Análisis del Escenario',
    internalLabel: 'Interno',
    externalLabel: 'Externo',
    selectRatingStars: 'Selecciona una valoración de 1 a 5 estrellas.',
    ratingSubmittedToast: '¡Valoración enviada! 🎉 Certificado disponible en la pestaña Certificados.',
    submitRatingError: 'Error al enviar la valoración. Inténtelo de nuevo.',
    courseCompletedSuccess: '¡Felicidades! ¡Has completado el curso con éxito!',
    stepCompleted: '¡Respuesta correcta! Etapa completada.',
    answerCorrectStepDone: 'Tu respuesta es correcta. Esta etapa se completó con éxito.',
    advanceNextLesson: '¡Muy bien! Avanza a la siguiente lección con el botón de abajo.',
    reviewQuiz: 'Revisar cuestionario',
    whatDidYouThink: '¿Qué te pareció el curso?',
    ratingHelperText: 'Tu valoración aparece debajo del mensaje de finalización y no cierra esta pantalla.',
    outOfFiveStars: 'de 5 estrellas',
    commentPlaceholder: 'Deja un comentario (opcional)',
    submitRating: 'Enviar valoración',
    thanksForRating: '¡Gracias por tu valoración!',
    correctOption: 'Correcta',
    incorrectAnswerTryAgain: 'Respuesta incorrecta. Inténtelo de nuevo.',

    // Checklist Block
    auditChecklistTitle: 'Lista de Verificación de Auditoría',
    verifiedCount: 'verificados',
    checklistHelper: 'Haz clic en la casilla para verificar, haz clic en la pregunta para ver los detalles',
    verifyLabel: 'Verificar',
    uncheckLabel: 'Desmarcar',
    evidenceToRequest: 'Evidencias a solicitar',
    checklistCompleteTitle: '¡Lista de verificación completa!',
    checklistCompleteMessage: 'Has revisado todos los puntos de verificación. Usa esta guía durante tus auditorías reales.',

    // Matching Block
    termsLabel: 'Términos',
    definitionsLabel: 'Definiciones',
    dragDefinitionHere: 'Arrastra una definición aquí',
    allDefinitionsMatched: 'Todas las definiciones han sido emparejadas',
    perfectAllCorrect: '🎉 ¡Perfecto! ¡Todas las asociaciones son correctas!',
    verifyAnswers: 'Verificar respuestas',

    // Scenario Block(s)
    situationLabel: 'Situación',
    questionWordLabel: 'Pregunta',
    viewAnswer: 'Ver respuesta',
    hideAnswer: 'Ocultar respuesta',
    answerLabel: 'Respuesta',
    expertTip: 'Consejo del experto',
    scenarioWord: 'Escenario',

    // PDF / Flip / Media Blocks
    downloadPdfMaterial: 'Descargar material PDF',
    clickToFlip: 'Haz clic para girar',
    definitionLabel: 'Definición',
    materialInProduction: 'Material en producción',
    openMaterial: 'Abrir material',

    // Sidebar
    collapseLabel: 'Contraer',
    expandLabel: 'Expandir',
    moduleLockedTooltip: '🔒 Módulo bloqueado',
    lockedLabel: 'Bloqueado',
    completedFemPlural: 'completadas',
    unlockModuleTooltip: 'Completa el cuestionario del módulo anterior con 70% o más para desbloquear',

    // Block Error Boundary
    blockRenderErrorTitle: 'No se pudo mostrar este contenido.',
    blockRenderErrorMessage: 'Avisa a soporte (kaio.souza@dqs.de) — el resto de la lección sigue disponible abajo.',
  },
};
